// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — PA Grants Project Workspace
// Full project detail view with 8 tabs (Screens 4-8)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Button, SimpleGrid, Table, Tabs,
  Loader, Center, Title, Progress, ActionIcon, Textarea, Modal, Checkbox,
  Divider, ThemeIcon, TextInput, Select, SegmentedControl, Popover, Tooltip,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconEdit, IconPlus, IconUpload, IconTrash,
  IconArrowLeft, IconCheck, IconClock, IconFile,
  IconFileTypePdf, IconFileSpreadsheet, IconRobot, IconFileDescription,
  IconCircleCheck, IconCircle, IconListCheck, IconCheckbox, IconScan,
  IconChevronDown, IconChevronRight, IconShieldCheck, IconDownload, IconPencil,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { exportToCSV } from '../../lib/exportUtils';
import { TABLES, FIELDS, createRecord, updateRecord, deleteRecord, callClaude, fetchTable } from '../../lib/api-switch';
import { useAuthStore } from '../../store/authStore';
import { useMyPAOrgIds } from '../../hooks/useMyPAOrgData';
import { useRoleStore } from '../../store/roleStore';
import {
  DAMAGE_CATEGORY_COLORS,
  PROJECT_STATUS_LABELS,
} from '../../types/pagrants';
import type { DamageCategory, ProjectStatus, TimelineEventType, PAProjectCost } from '../../types/pagrants';
import MitigationTab from './MitigationTab';
import ProjectPhotos from './ProjectPhotos';
import Contracts from './Contracts';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

// ── Formatters & color maps ──────────────────────────────────

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const COST_TYPE_COLORS: Record<string, string> = {
  labor: 'violet', equipment: 'orange', materials: 'teal', contracts: 'blue',
  rented_equip: 'cyan', donated: 'pink', other: 'gray',
};

const COST_TYPE_LABELS: Record<string, string> = {
  labor: 'Labor', equipment: 'Equipment', materials: 'Materials',
  contracts: 'Contracts', rented_equip: 'Rented Equip', donated: 'Donated', other: 'Other',
};

const TIMELINE_EVENT_COLORS: Record<string, string> = {
  milestone: 'blue', deadline: 'red', submission: 'teal', approval: 'green',
  inspection: 'orange', meeting: 'violet', correspondence: 'gray',
};

const num = (v: string | undefined) => parseFloat(v || '0') || 0;

/** Format ISO timestamps or date strings to "MMM D, YYYY" */
function fmtDate(v: string | undefined | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Lightweight markdown-to-HTML for DDD/SOW display (no dependencies) */
function renderSimpleMarkdown(text: string): string {
  // Escape HTML entities
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Restore newlines: markdown headings, ---, and | as section breaks
  html = html.replace(/\s*(#{1,3})\s+/g, '\n$1 ');
  html = html.replace(/\s*---\s*/g, '\n---\n');
  html = html.replace(/\s*\|\s*/g, ' | ');
  // Add breaks before common section patterns
  html = html.replace(/\s*(\*\*\d+\.\d+)/g, '\n\n$1');
  html = html.replace(/\s*(\*\*Division|\*\*Note:|\*\*Total|\*\*Subtotal)/g, '\n\n$1');
  // Add break before bold labels that start sections (e.g., **Gymnasium**, **HVAC**)
  html = html.replace(/\s*(\*\*[A-Z][^*]{2,40}\*\*:?)\s/g, '\n$1 ');
  // Add break before list-like items (- item)
  html = html.replace(/\s+-\s+/g, '\n- ');
  // Convert markdown formatting
  html = html.replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 4px;font-size:14px">$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 6px;font-size:15px">$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2 style="margin:16px 0 8px;font-size:16px">$1</h2>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/^---$/gm, '<hr style="margin:8px 0;border:none;border-top:1px solid #ddd"/>');
  html = html.replace(/^- (.+)$/gm, '<li style="margin-left:16px">$1</li>');
  // Newlines to <br> / paragraphs
  html = html.replace(/\n{2,}/g, '</p><p style="margin:8px 0">');
  html = html.replace(/\n/g, '<br/>');
  return '<p style="margin:0">' + html + '</p>';
}

function formatCurrency(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  const whole = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts.length > 1) return `$${formatted}.${parts[1].slice(0, 2)}`;
  return formatted ? `$${formatted}` : '';
}
function stripCurrency(formatted: string): string {
  return formatted.replace(/[$,]/g, '');
}

// ── Cost form type ───────────────────────────────────────────

interface CostFormState {
  costType: string;
  description: string;
  quantity: string;
  unit: string;
  unitCost: string;
  totalCost: string;
  cefPart: string;
  vendorEmployee: string;
  sourceReference: string;
  estimatedOrActual: string;
  eligible: string;
  notes: string;
}

const emptyCostForm = (): CostFormState => ({
  costType: '', description: '', quantity: '', unit: '',
  unitCost: '', totalCost: '', cefPart: '', vendorEmployee: '', sourceReference: '',
  estimatedOrActual: 'estimated', eligible: 'true', notes: '',
});

// ── Insurance form type ──────────────────────────────────────

interface InsFormState {
  policyNumber: string; carrier: string; policyType: string;
  coverageAmount: string; deductible: string;
  policyEffectiveDate: string; policyExpirationDate: string;
  claimFiled: string; claimNumber: string; claimDate: string;
  settlementAmount: string; settlementDate: string;
  denialReceived: string; denialReason: string;
  obtainMaintainRequired: string; obtainMaintainCommitted: string;
  femaDeductionAmount: string;
}

const emptyInsForm = (): InsFormState => ({
  policyNumber: '', carrier: '', policyType: '',
  coverageAmount: '', deductible: '',
  policyEffectiveDate: '', policyExpirationDate: '',
  claimFiled: 'no', claimNumber: '', claimDate: '',
  settlementAmount: '', settlementDate: '',
  denialReceived: 'no', denialReason: '',
  obtainMaintainRequired: 'no', obtainMaintainCommitted: 'no',
  femaDeductionAmount: '',
});

// ── Compliance form type ─────────────────────────────────────

interface CompFormState {
  nepaRequired: string; nepaLevel: string; nepaStatus: string; nepaCompletionDate: string;
  section106Required: string; section106Status: string;
  historicProperty: string; shpoConsultation: string; tribalConsultation: string;
  inFloodplain: string; floodZone: string;
  floodInsuranceRequired: string; eo11988Compliance: string;
  coastalBarrierArea: string; coastalZone: string;
  esaConsultationRequired: string; hazmatPresent: string;
}

const emptyCompForm = (): CompFormState => ({
  nepaRequired: '', nepaLevel: '', nepaStatus: '', nepaCompletionDate: '',
  section106Required: '', section106Status: '',
  historicProperty: '', shpoConsultation: '', tribalConsultation: '',
  inFloodplain: '', floodZone: '',
  floodInsuranceRequired: '', eo11988Compliance: '',
  coastalBarrierArea: '', coastalZone: '',
  esaConsultationRequired: '', hazmatPresent: '',
});

// ── Timeline event form type ─────────────────────────────────

interface EventFormState {
  eventType: string; eventDate: string; dueDate: string;
  description: string; status: string;
}

const emptyEventForm = (): EventFormState => ({
  eventType: 'milestone', eventDate: '', dueDate: '',
  description: '', status: 'pending',
});

// ── Edit Project form type ───────────────────────────────────

interface EditProjectFormState {
  projectNumber: string; projectTitle: string;
  category: string; projectSize: string; workType: string;
  projectAddress: string;
  damageDescription: string; scopeOfWork: string; costNarrative: string;
  estimatedCost: string; actualCost: string; federalSharePct: string;
  workStartDate: string; deadline: string; completionPctEstimate: string;
  assignedSpecialist: string; status: string; phase: string; notes: string;
}

const emptyEditProjectForm = (): EditProjectFormState => ({
  projectNumber: '', projectTitle: '',
  category: '', projectSize: '', workType: '',
  projectAddress: '',
  damageDescription: '', scopeOfWork: '', costNarrative: '',
  estimatedCost: '', actualCost: '', federalSharePct: '75',
  workStartDate: '', deadline: '', completionPctEstimate: '',
  assignedSpecialist: '', status: 'draft', phase: 'formulation', notes: '',
});

// ── Select option arrays ─────────────────────────────────────

const COST_TYPE_OPTIONS = [
  { value: 'labor', label: 'Labor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'rented_equip', label: 'Rented Equipment' },
  { value: 'donated', label: 'Donated' },
  { value: 'other', label: 'Other' },
];

const EST_ACTUAL_OPTIONS = [
  { value: 'estimated', label: 'Estimated' },
  { value: 'actual', label: 'Actual' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const ELIGIBLE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const NEPA_LEVEL_OPTIONS = [
  { value: 'catex', label: 'CatEx' },
  { value: 'ea', label: 'EA' },
  { value: 'eis', label: 'EIS' },
];

const COMPLIANCE_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_required', label: 'Not Required' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'milestone', label: 'Milestone' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'submission', label: 'Submission' },
  { value: 'approval', label: 'Approval' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'correspondence', label: 'Correspondence' },
];

const EVENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
];

const CATEGORY_OPTIONS = [
  { value: 'A', label: 'A - Debris Removal' },
  { value: 'B', label: 'B - Emergency Protective Measures' },
  { value: 'C', label: 'C - Roads & Bridges' },
  { value: 'D', label: 'D - Water Control Facilities' },
  { value: 'E', label: 'E - Buildings & Equipment' },
  { value: 'F', label: 'F - Utilities' },
  { value: 'G', label: 'G - Parks & Recreation' },
];

const SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'large', label: 'Large' },
];

const PROJECT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'obligated', label: 'Obligated' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'appealed', label: 'Appealed' },
];

const PHASE_OPTIONS = [
  { value: 'formulation', label: 'Formulation' },
  { value: 'review', label: 'Review' },
  { value: 'obligation', label: 'Obligation' },
  { value: 'work_in_progress', label: 'Work in Progress' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'appeal', label: 'Appeal' },
];

const WORK_TYPE_OPTIONS = [
  { value: 'emergency_work', label: 'Emergency Work (Cat A/B)' },
  { value: 'permanent_repair', label: 'Permanent Repair' },
  { value: 'permanent_replace', label: 'Permanent Replacement' },
  { value: 'improved_project', label: 'Improved Project (406)' },
  { value: 'alternate_project', label: 'Alternate Project (428)' },
  { value: 'debris_removal', label: 'Debris Removal' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures' },
];

const CEF_PART_OPTIONS = [
  { value: 'A', label: 'Part A \u2013 Force Account Labor' },
  { value: 'B', label: 'Part B \u2013 Force Account Equipment' },
  { value: 'C', label: 'Part C \u2013 Materials' },
  { value: 'D', label: 'Part D \u2013 Rented Equipment' },
  { value: 'E', label: 'Part E \u2013 Contract Work' },
  { value: 'F', label: 'Part F \u2013 Direct Administrative Costs' },
];

const POLICY_TYPE_OPTIONS = [
  { value: 'property', label: 'Property' },
  { value: 'flood', label: 'Flood (NFIP)' },
  { value: 'wind', label: 'Wind/Storm' },
  { value: 'liability', label: 'General Liability' },
  { value: 'vehicle', label: 'Vehicle/Equipment' },
  { value: 'other', label: 'Other' },
];

// ── PA Project Checklist Items ────────────────────────────────
// Standard checklist of forms, documents, reviews, and milestones
// that every PA project needs to complete.

interface ChecklistItem {
  key: string;
  label: string;
  category: 'document' | 'form' | 'review' | 'milestone';
  description: string;
  required: boolean;
}

const PA_PROJECT_CHECKLIST_FALLBACK: ChecklistItem[] = [
  // Documents
  { key: 'damage_photos', label: 'Damage Photos (Pre/Post)', category: 'document', description: 'Photographs documenting damage before and after disaster', required: true },
  { key: 'ddd', label: 'Damage Description & Dimensions (DDD)', category: 'document', description: 'Detailed damage description including facility name, pre-disaster condition, and measurements', required: true },
  { key: 'sow', label: 'Scope of Work (SOW)', category: 'document', description: 'Detailed scope including materials, methods, labor, equipment, and quantities', required: true },
  { key: 'cost_estimate', label: 'Cost Estimate / CEF', category: 'document', description: 'FEMA Cost Estimating Format breakdown with supporting documentation', required: true },
  { key: 'insurance_docs', label: 'Insurance Documentation', category: 'document', description: 'Insurance policies, claim filings, settlement letters, or denial documentation', required: true },
  { key: 'procurement_docs', label: 'Procurement Documentation', category: 'document', description: 'Bid solicitations, proposals, contracts, and 2 CFR 200 compliance records', required: false },
  { key: 'invoices', label: 'Invoices & Receipts', category: 'document', description: 'Vendor invoices, material receipts, rental agreements, and proof of payment', required: false },
  { key: 'force_account_labor', label: 'Force Account Labor Records', category: 'document', description: 'Timesheets, payroll records, and fringe benefit documentation', required: false },
  { key: 'force_account_equip', label: 'Force Account Equipment Records', category: 'document', description: 'Equipment logs, FEMA rate schedule justification, operator records', required: false },
  { key: 'ownership_docs', label: 'Legal Responsibility / Ownership', category: 'document', description: 'Proof of legal responsibility for the damaged facility (deeds, leases, maintenance agreements)', required: true },
  // Forms
  { key: 'applicant_certification', label: 'Applicant Certification', category: 'form', description: 'Signed certification of legal responsibility, insurance, procurement compliance, and no duplication', required: true },
  { key: 'sf424', label: 'SF-424 Application for Federal Assistance', category: 'form', description: 'Standard federal grant application form', required: false },
  // Reviews
  { key: 'ehp_review', label: 'EHP / NEPA Review', category: 'review', description: 'Environmental and Historic Preservation review (NEPA, Section 106, floodplain, ESA)', required: true },
  { key: 'hazard_mitigation_review', label: 'Hazard Mitigation Review', category: 'review', description: 'Review of 406 hazard mitigation proposal and benefit-cost analysis', required: false },
  { key: 'insurance_review', label: 'Insurance Review / O&M', category: 'review', description: 'FEMA insurance specialist review of obtain-and-maintain requirements', required: true },
  { key: 'cost_review', label: 'Cost Reasonableness Review', category: 'review', description: 'Review that project costs are reasonable, necessary, and directly tied to the disaster', required: true },
  // Milestones
  { key: 'site_inspection', label: 'Site Inspection Complete', category: 'milestone', description: 'FEMA/state site inspection of damage has been completed', required: true },
  { key: 'project_formulated', label: 'Project Worksheet Formulated', category: 'milestone', description: 'PW has been drafted and validated with all required documentation', required: true },
  { key: 'project_obligated', label: 'Project Obligated', category: 'milestone', description: 'Federal funds have been obligated for this project', required: true },
  { key: 'work_completed', label: 'Work Completed', category: 'milestone', description: 'All repair/replacement work has been physically completed', required: false },
  { key: 'closeout_submitted', label: 'Closeout Package Submitted', category: 'milestone', description: 'Final documentation, certifications, and reconciliation submitted for closeout', required: false },
];

const CHECKLIST_CATEGORY_COLORS: Record<string, string> = {
  document: 'blue', form: 'violet', review: 'orange', milestone: 'green',
};
const CHECKLIST_CATEGORY_LABELS: Record<string, string> = {
  document: 'Document', form: 'Form', review: 'Review', milestone: 'Milestone',
};

// ── FEMA Special Consideration Questions (009-0-120) ─────────
const SCQ_QUESTIONS = [
  { key: 'scq1', text: '1. Does the damaged facility or item of work have insurance and/or is it an insurable risk? (e.g., buildings, equipment, vehicles)', compField: 'floodInsuranceRequired' as const },
  { key: 'scq2', text: '2. Is the damaged facility located within a floodplain or coastal high hazard area, or does it have an impact on a floodplain or wetland?', compField: 'inFloodplain' as const },
  { key: 'scq3', text: '3. Is the damaged facility or item of work located within or adjacent to a Coastal Barrier Resource System Unit or an Otherwise Protected Area?', compField: 'coastalBarrierArea' as const },
  { key: 'scq4', text: '4. Will the proposed facility repairs/reconstruction change the pre-disaster condition? (e.g., footprint, material, location, capacity, use or function)', compField: 'eo11988Compliance' as const },
  { key: 'scq5', text: '5. Does the applicant have a hazard mitigation proposal or would the applicant like technical assistance for a hazard mitigation proposal?', compField: null },
  { key: 'scq6', text: '6. Is the damaged facility on the National Register of Historic Places or the state historic listing? Is it older than 50 years? Are there other, similar buildings near the site?', compField: 'historicProperty' as const },
  { key: 'scq7', text: '7. Is there any contamination or hazardous material at or near the site? (e.g., underground storage tanks, asbestos, lead paint)', compField: 'hazmatPresent' as const },
  { key: 'scq8', text: '8. Are there any environmental or ecological concerns? (e.g., endangered species, critical habitat, wetlands, wild and scenic rivers)', compField: 'esaConsultationRequired' as const },
];

interface ScqAnswers {
  [key: string]: { answer: string; comment: string };
}

// Document type options built dynamically from checklist items (moved into component as useMemo)

function deriveDocType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'PDF', doc: 'Document', docx: 'Document', xlsx: 'Spreadsheet',
    xls: 'Spreadsheet', csv: 'Spreadsheet', pptx: 'Presentation',
    ppt: 'Presentation', png: 'Image', jpg: 'Image', jpeg: 'Image',
    gif: 'Image', zip: 'Archive', rar: 'Archive',
  };
  return map[ext] || 'Other';
}

// ── PA Task Templates ────────────────────────────────────────

const PA_TASK_TEMPLATES: Array<{ name: string; tasks: Array<{ description: string; daysFromStart: number }> }> = [
  {
    name: 'Standard PA Project Setup',
    tasks: [
      { description: 'Complete Preliminary Damage Assessment (PDA)', daysFromStart: 0 },
      { description: 'Submit Request for Public Assistance (RPA)', daysFromStart: 3 },
      { description: 'Schedule and complete FEMA site inspection', daysFromStart: 7 },
      { description: 'Prepare Project Worksheet with cost estimate', daysFromStart: 14 },
      { description: 'Submit Project Worksheet for FEMA review', daysFromStart: 21 },
      { description: 'Complete environmental/historic (EHP) review', daysFromStart: 30 },
      { description: 'Obtain insurance documentation and file claims', daysFromStart: 14 },
      { description: 'Complete procurement for contracted work', daysFromStart: 45 },
      { description: 'Begin permanent repair work', daysFromStart: 60 },
      { description: 'Submit quarterly progress reports', daysFromStart: 90 },
    ],
  },
  {
    name: 'Emergency Work (Cat A/B)',
    tasks: [
      { description: 'Document pre-disaster conditions', daysFromStart: 0 },
      { description: 'Begin emergency protective measures', daysFromStart: 0 },
      { description: 'Photograph all damage before cleanup', daysFromStart: 1 },
      { description: 'Track force account labor hours daily', daysFromStart: 1 },
      { description: 'Log equipment usage with FEMA rates', daysFromStart: 1 },
      { description: 'Collect material receipts and invoices', daysFromStart: 3 },
      { description: 'Submit debris monitoring reports', daysFromStart: 7 },
      { description: 'Complete emergency work summary', daysFromStart: 14 },
    ],
  },
  {
    name: 'Large Project Closeout',
    tasks: [
      { description: 'Compile all invoices and payment documentation', daysFromStart: 0 },
      { description: 'Complete final inspection with FEMA specialist', daysFromStart: 7 },
      { description: 'Reconcile actual costs vs. obligated amount', daysFromStart: 14 },
      { description: 'Submit final claim with supporting documentation', daysFromStart: 21 },
      { description: 'Resolve any audit findings or questioned costs', daysFromStart: 30 },
      { description: 'Obtain final insurance settlement documentation', daysFromStart: 30 },
      { description: 'Submit project completion certification', daysFromStart: 45 },
    ],
  },
];

// ── Component ────────────────────────────────────────────────

export default function ProjectWorkspace() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const navigate = useNavigate();
  const [costFilter, setCostFilter] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  const {
    organizations, disasters, damageItems, projects, projectCosts,
    insurance, compliance, documents, timelineEvents, notes,
    timeExtensions, reimbursementRequests, laborRecords,
    loadingProjects, fetchAll, fetchLabor, fetchProjectCosts,
    fetchInsurance, fetchCompliance, fetchTimeline, fetchNotes, fetchDocuments,
    fetchEquipment, fetchMaterials, fetchContracts, fetchTimeExtensions,
    createProjectCost, updateProjectCost, deleteProjectCost,
    updateProject, createTimelineEvent, updateTimelineEvent, deleteTimelineEvent,
    deleteDocument, updateDocument, uploadDocumentFile, getDocumentDownloadUrl,
  } = usePAGrantsStore();
  const { token } = useAuthStore();
  const myOrgIds = useMyPAOrgIds();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'].includes(effectiveRole?.roleSlug ?? '');
  const isApplicantViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';
  void isApplicantViewer; // used by conditional UI below

  useEffect(() => {
    fetchAll();
  }, []);

  // ── Dynamic checklist from database ───────────────────────
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(PA_PROJECT_CHECKLIST_FALLBACK);
  useEffect(() => {
    (async () => {
      try {
        const FC = FIELDS.paChecklistTemplates;
        const raw = await fetchTable<Record<string, unknown>>(TABLES.paChecklistTemplates);
        const paItems = raw
          .filter(r => String(r[FC.isActive] ?? '') === 'true' && (String(r[FC.programType] ?? '') === 'pa' || String(r[FC.programType] ?? '') === 'both'))
          .sort((a, b) => (parseInt(String(a[FC.sortOrder] ?? '0')) || 0) - (parseInt(String(b[FC.sortOrder] ?? '0')) || 0))
          .map(r => ({
            key: String(r[FC.itemKey] ?? ''),
            label: String(r[FC.label] ?? ''),
            category: String(r[FC.category] ?? 'document') as ChecklistItem['category'],
            description: String(r[FC.description] ?? ''),
            required: String(r[FC.required] ?? '') === 'true',
          }));
        if (paItems.length > 0) setChecklistItems(paItems);
      } catch { /* fallback to hardcoded */ }
    })();
  }, []);

  const DOC_TYPE_OPTIONS = useMemo(() => [
    { value: '', label: '— Select document type —' },
    ...checklistItems
      .filter(c => c.category === 'document')
      .map(c => ({ value: c.key, label: c.label })),
    { value: 'other', label: 'Other' },
  ], [checklistItems]);

  // ── Cost modal state ──────────────────────────────────────
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<PAProjectCost | null>(null);
  const [costForm, setCostForm] = useState<CostFormState>(emptyCostForm());
  const [costSaving, setCostSaving] = useState(false);

  // ── Insurance modal state ─────────────────────────────────
  const [insModalOpen, setInsModalOpen] = useState(false);
  const [editingIns, setEditingIns] = useState<{ Rid: number } | null>(null);
  const [insForm, setInsForm] = useState<InsFormState>(emptyInsForm());
  const [insSaving, setInsSaving] = useState(false);

  // ── Compliance modal state ────────────────────────────────
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [compForm, setCompForm] = useState<CompFormState>(emptyCompForm());
  const [compSaving, setCompSaving] = useState(false);

  // Compliance dependency auto-enforcement
  useEffect(() => {
    setCompForm(prev => {
      const next = { ...prev };
      let changed = false;
      // Historic property = yes → Section 106 required
      if (prev.historicProperty === 'yes' && prev.section106Required !== 'yes') {
        next.section106Required = 'yes'; changed = true;
      }
      // In floodplain = yes → flood insurance required, EO 11988 compliance required
      if (prev.inFloodplain === 'yes') {
        if (prev.floodInsuranceRequired !== 'yes') { next.floodInsuranceRequired = 'yes'; changed = true; }
        if (prev.eo11988Compliance !== 'yes') { next.eo11988Compliance = 'yes'; changed = true; }
      }
      // Hazmat present = yes → NEPA required
      if (prev.hazmatPresent === 'yes' && prev.nepaRequired !== 'yes') {
        next.nepaRequired = 'yes'; changed = true;
      }
      return changed ? next : prev;
    });
  }, [compForm.historicProperty, compForm.inFloodplain, compForm.hazmatPresent]);

  // ── Timeline event modal state ────────────────────────────
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState<EventFormState>(emptyEventForm());
  const [eventSaving, setEventSaving] = useState(false);

  // ── Edit Project modal state ──────────────────────────────
  const [editProjModalOpen, setEditProjModalOpen] = useState(false);
  const [editProjForm, setEditProjForm] = useState<EditProjectFormState>(emptyEditProjectForm());
  const [editProjSaving, setEditProjSaving] = useState(false);
  const [editProjTab, setEditProjTab] = useState<string | null>('general');

  // ── Notes saving state ────────────────────────────────────
  const [noteSaving, setNoteSaving] = useState(false);

  // ── Task modal state ────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ description: '', dueDate: '', status: 'pending' });
  const [taskSaving, setTaskSaving] = useState(false);
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);

  // ── Document upload state ──────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const [viewDoc, setViewDoc] = useState<any>(null);
  const [docFileAvailable, setDocFileAvailable] = useState<boolean | null>(null); // null=checking, true=ok, false=unavailable
  const [editingDoc, setEditingDoc] = useState(false);
  const [editDocForm, setEditDocForm] = useState({ title: '', documentType: '', tags: '', notes: '' });
  const [editDocFile, setEditDocFile] = useState<File | null>(null);
  const editDocFileRef = useRef<HTMLInputElement>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiDocResult, setAiDocResult] = useState<string>('');

  // ── Checklist completion modal state ───────────────────────
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [checkingItem, setCheckingItem] = useState<ChecklistItem | null>(null);
  const [checkNotes, setCheckNotes] = useState('');
  const [checkSaving, setCheckSaving] = useState(false);

  // ── Revision request state ──────────────────────────────────
  const [revisionItem, setRevisionItem] = useState<ChecklistItem | null>(null);
  const [revisionNote, setRevisionNote] = useState('');
  const [revisionSaving, setRevisionSaving] = useState(false);

  // ── AI Cost Extraction state ─────────────────────────────────
  const [extracting, setExtracting] = useState(false);
  const [extractedCosts, setExtractedCosts] = useState<CostFormState[]>([]);
  const [extractModalOpen, setExtractModalOpen] = useState(false);
  const [extractSource, setExtractSource] = useState('');

  // ── AI Assist state ────────────────────────────────────────────
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistResult, setAiAssistResult] = useState('');

  // ── Special Consideration Questions state ────────────────────
  const [scqAnswers, setScqAnswers] = useState<ScqAnswers>({});
  const [scqSaving, setScqSaving] = useState(false);
  const [scqExpanded, setScqExpanded] = useState(true);

  // ── AI Scan Receipt state ─────────────────────────────────────
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [scannedCosts, setScannedCosts] = useState<CostFormState[]>([]);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanSource, setScanSource] = useState('');

  // ── Timesheet import state ─────────────────────────────────
  const [timesheetImportOpen, setTimesheetImportOpen] = useState(false);
  const [timesheetSelected, setTimesheetSelected] = useState<Set<number>>(new Set());
  const [timesheetImporting, setTimesheetImporting] = useState(false);
  const [tsSearch, setTsSearch] = useState('');
  const [tsCatFilter, setTsCatFilter] = useState<string | null>(null);

  // ── Derived data ───────────────────────────────────────────

  const project = useMemo(
    () => projects.find(p => p.Rid === Number(projectId)),
    [projects, projectId],
  );

  const org = useMemo(
    () => organizations.find(o => o.Rid === Number(orgId)),
    [organizations, orgId],
  );

  const disaster = useMemo(
    () => project ? disasters.find(d => String(d.Rid) === project.disasterId) : undefined,
    [disasters, project],
  );

  const projRid = String(project?.Rid ?? '');

  // Load all project sub-records once project resolves — must be after project useMemo to avoid TDZ
  useEffect(() => {
    if (!project?.Rid) return;
    const id = project.Rid;
    fetchProjectCosts(id);
    fetchInsurance(id);
    fetchCompliance(id);
    fetchTimeline(id);
    fetchNotes(id);
    fetchDocuments(id);
    fetchEquipment(id);
    fetchMaterials(id);
    fetchContracts(id);
    fetchLabor();
    fetchTimeExtensions(id);
  }, [project?.Rid]);

  const projDamageItems = useMemo(
    () => damageItems.filter(d => d.projectId === projRid),
    [damageItems, projRid],
  );

  const projCosts = useMemo(() => {
    const filtered = projectCosts.filter(c => c.projectId === projRid);
    if (!costFilter) return filtered;
    return filtered.filter(c => c.costType === costFilter);
  }, [projectCosts, projRid, costFilter]);

  const projInsurance = useMemo(
    () => insurance.filter(i => i.projectId === projRid),
    [insurance, projRid],
  );

  const projCompliance = useMemo(
    () => compliance.filter(c => c.projectId === projRid),
    [compliance, projRid],
  );

  const projDocuments = useMemo(
    () => documents.filter(d => d.projectId === projRid),
    [documents, projRid],
  );

  const projTimeline = useMemo(
    () => timelineEvents.filter(t => t.projectId === projRid)
      .sort((a, b) => (a.eventDate || '').localeCompare(b.eventDate || '')),
    [timelineEvents, projRid],
  );

  const projNotes = useMemo(
    () => notes.filter(n => String(n.projectId) === projRid),
    [notes, projRid],
  );

  const projTasks = useMemo(
    () => timelineEvents
      .filter(t => t.projectId === projRid && (t.eventType === 'task' || t.eventType === 'deadline'))
      .sort((a, b) => (a.dueDate || '9').localeCompare(b.dueDate || '9')),
    [timelineEvents, projRid],
  );

  const todayStr = new Date().toISOString().split('T')[0];

  // Cost summary by type
  const costsByType = useMemo(() => {
    const all = projectCosts.filter(c => c.projectId === projRid);
    const groups: Record<string, number> = {};
    for (const c of all) {
      const t = c.costType || 'other';
      groups[t] = (groups[t] || 0) + num(c.totalCost);
    }
    return groups;
  }, [projectCosts, projRid]);

  // Labor records assigned to this project
  const projLabor = useMemo(
    () => laborRecords.filter(r => r.projectId === projRid),
    [laborRecords, projRid],
  );

  // Unassigned labor records for import (only supervisor-approved)
  const unassignedLabor = useMemo(() => {
    let filtered = laborRecords.filter(r => !r.projectId && r.timesheetOnFile === 'approved');
    if (tsSearch) {
      const q = tsSearch.toLowerCase();
      filtered = filtered.filter(r =>
        (r.employeeName || '').toLowerCase().includes(q) ||
        (r.activityDescription || '').toLowerCase().includes(q)
      );
    }
    if (tsCatFilter) {
      filtered = filtered.filter(r => r.category === tsCatFilter);
    }
    return filtered.sort((a, b) => (b.workDate || '').localeCompare(a.workDate || ''));
  }, [laborRecords, tsSearch, tsCatFilter]);

  // Labor assigned to OTHER projects (for grayed-out display)
  const otherProjectLabor = useMemo(
    () => laborRecords.filter(r => r.projectId && r.projectId !== projRid),
    [laborRecords, projRid],
  );

  // Documents grouped by type
  const docsByType = useMemo(() => {
    const groups: Record<string, typeof projDocuments> = {};
    for (const d of projDocuments) {
      const t = d.documentType || 'Other';
      if (!groups[t]) groups[t] = [];
      groups[t].push(d);
    }
    return groups;
  }, [projDocuments]);

  // ── Checklist completion tracking ───────────────────────────
  // A checklist item is "done" if a matching document tag or timeline event exists
  // A revision request overrides "done" — item needs rework
  const checklistStatus = useMemo(() => {
    const status = new Map<string, { done: boolean; revision?: boolean; revisionNote?: string; revisionBy?: string; revisionDate?: string; date?: string; by?: string; docRid?: number }>();
    for (const item of checklistItems) {
      const matchingDoc = projDocuments.find(d => d.tags === item.key);
      const matchingEvent = projTimeline.find(t =>
        t.description?.includes(`[checklist:${item.key}]`)
      );
      // Check for revision requests — most recent by Rid (higher Rid = created later)
      const revisionEvents = projTimeline
        .filter(t => t.description?.includes(`[revision:${item.key}]`))
        .sort((a, b) => b.Rid - a.Rid);
      const latestRevision = revisionEvents[0];
      // Revision is active if it's newer than the completion event (both are timeline events, so Rid comparison works)
      // For documents, compare revision date >= upload date (same-day revision should win)
      const completionEventRid = matchingEvent?.Rid ?? 0;
      const hasRevision = latestRevision && (
        latestRevision.Rid > completionEventRid &&
        (!matchingDoc || latestRevision.eventDate >= (matchingDoc.uploadDate || ''))
      );

      if (hasRevision) {
        const note = latestRevision.description.replace(/\[revision:[^\]]+\]\s*/, '').replace(/^—\s*/, '');
        status.set(item.key, { done: false, revision: true, revisionNote: note, revisionBy: latestRevision.creatorId, revisionDate: latestRevision.eventDate });
      } else if (matchingDoc) {
        status.set(item.key, { done: true, date: matchingDoc.uploadDate, by: matchingDoc.uploadedBy, docRid: matchingDoc.Rid });
      } else if (matchingEvent) {
        status.set(item.key, { done: true, date: matchingEvent.eventDate, by: matchingEvent.creatorId });
      } else {
        status.set(item.key, { done: false });
      }
    }
    return status;
  }, [projDocuments, projTimeline, checklistItems]);

  const checklistDone = useMemo(() =>
    checklistItems.filter(c => checklistStatus.get(c.key)?.done).length,
    [checklistStatus, checklistItems],
  );
  const checklistRequired = useMemo(() => checklistItems.filter(c => c.required).length, [checklistItems]);
  const checklistRequiredDone = useMemo(() =>
    checklistItems.filter(c => c.required && checklistStatus.get(c.key)?.done).length,
    [checklistStatus, checklistItems],
  );

  // ── Initialize SCQ from compliance record ──────────────────
  const scqInitialized = useRef(false);
  useEffect(() => {
    if (scqInitialized.current || projCompliance.length === 0) return;
    scqInitialized.current = true;
    const comp = projCompliance[0];
    // Parse stored SCQ data from complianceNotes — handles both formats:
    // Format A (from save): {scq1: "comment text", scq2: "comment"}
    // Format B (from seed): {scq1: {answer: "Yes", comment: "text"}}
    let storedData: Record<string, { answer?: string; comment?: string } | string> = {};
    try {
      if (comp.complianceNotes) storedData = JSON.parse(comp.complianceNotes);
    } catch { /* not JSON, ignore */ }

    const answers: ScqAnswers = {};
    for (const q of SCQ_QUESTIONS) {
      const fieldVal = q.compField ? (comp as unknown as Record<string, string>)[q.compField] || '' : '';
      const stored = storedData[q.key];
      let answer = '';
      let comment = '';
      if (typeof stored === 'object' && stored !== null) {
        answer = stored.answer || '';
        comment = stored.comment || '';
      } else if (typeof stored === 'string') {
        comment = stored;
      }
      // Field value takes precedence for answer if available
      if (fieldVal) {
        const norm = fieldVal.toLowerCase();
        answer = norm === 'yes' ? 'yes' : norm === 'no' ? 'no' : norm === 'unsure' ? 'unsure' : answer;
      } else if (answer) {
        const norm = answer.toLowerCase();
        answer = norm === 'yes' ? 'yes' : norm === 'no' ? 'no' : norm === 'unsure' ? 'unsure' : '';
      }
      answers[q.key] = { answer, comment };
    }
    // Q5 uses project-level hazardMitigationIncluded
    if (project) {
      const hm = (project.hazardMitigationIncluded || '').toLowerCase();
      const existingComment = answers.scq5?.comment || '';
      answers.scq5 = { answer: hm === 'yes' ? 'yes' : hm === 'no' ? 'no' : '', comment: existingComment };
    }
    setScqAnswers(answers);
  }, [projCompliance, project]);

  // ── Document upload handlers ────────────────────────────────

  function onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    // Don't clear uploadDocType here — checklist buttons pre-set it before triggering this input
    setUploadNotes('');
    setAiDocResult('');
    setUploadModalOpen(true);
    e.target.value = '';
  }

  async function handleAiAnalyze() {
    if (!uploadFile) return;
    setAiAnalyzing(true);
    setAiDocResult('');
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const isImage = uploadFile.type.startsWith('image/');
      const isPdf = uploadFile.type === 'application/pdf';

      const contentBlocks: any[] = [];
      if (isImage) {
        contentBlocks.push({ type: 'image', source: { type: 'base64', media_type: uploadFile.type, data: base64 } });
      } else if (isPdf) {
        contentBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } });
      }
      contentBlocks.push({ type: 'text', text: `Analyze this document for a FEMA Public Assistance disaster recovery project. The document is named "${uploadFile.name}".

Identify what type of document this is (insurance policy, adjuster report, invoice, receipt, contract, inspection report, environmental assessment, photo, permit, etc.).

Extract any key data that should be entered into the project management system, such as:
- Insurance: policy numbers, carrier, coverage amounts, claim numbers, settlement amounts
- Contracts: contractor name, contract number, amounts, dates, scope
- Invoices: vendor, line items, quantities, costs, invoice numbers
- Inspection reports: inspector name, date, findings, recommendations
- Environmental: NEPA level, compliance status, protected species findings
- Any other relevant data points

Format your response as a clear summary with the document type at the top, then the extracted data as a bulleted list. Be specific with numbers, dates, and names.` });

      const result = await callClaude([
        { role: 'user', content: contentBlocks },
      ], {
        system: 'You are a document analysis assistant for FEMA Public Assistance grant management. Extract structured data from documents accurately. Only report what you can actually see in the document — do not make up values.',
        maxTokens: 2000,
      });
      setAiDocResult(result);
    } catch (err) {
      setAiDocResult(`Analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setAiAnalyzing(false);
    }
  }

  async function handleAiExtractCosts() {
    if (!uploadFile) return;
    setAiAnalyzing(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const isImage = uploadFile.type.startsWith('image/');
      const isPdf = uploadFile.type === 'application/pdf';
      const contentBlocks: any[] = [];
      if (isImage) {
        contentBlocks.push({ type: 'image', source: { type: 'base64', media_type: uploadFile.type, data: base64 } });
      } else if (isPdf) {
        contentBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } });
      }
      contentBlocks.push({ type: 'text', text: `Extract ALL cost line items from this document. This is for a FEMA Public Assistance disaster recovery project.

For each line item return a JSON object with these fields:
- description: string (what the item is)
- quantity: string (number)
- unit: string (EA, LF, SF, HR, LS, etc.)
- unitCost: string (dollar amount without $ sign)
- totalCost: string (dollar amount without $ sign)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Admin)

Return ONLY a raw JSON array. No explanation, no markdown fences. Extract the REAL numbers from the document — do NOT make up values. If you cannot read specific values, use "0".` });

      const result = await callClaude([
        { role: 'user', content: contentBlocks },
      ], {
        system: 'You extract cost data from invoices, receipts, and financial documents for FEMA PA grant management. Return only valid JSON arrays. Never fabricate data.',
        maxTokens: 4096,
      });

      // Parse JSON from response
      let cleaned = result.trim();
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      const match = cleaned.match(/\[[\s\S]*\]/);
      if (!match) throw new Error('No cost data found in document');
      const items = JSON.parse(match[0]);
      if (!Array.isArray(items) || items.length === 0) throw new Error('No line items extracted');

      const costForms: CostFormState[] = items.map((item: any) => ({
        costType: item.costType || 'other',
        cefPart: item.cefPart || 'F',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        vendorEmployee: '',
        sourceReference: uploadFile.name,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Extracted by AI from ${uploadFile.name}`,
      }));

      setScanSource(uploadFile.name);
      setScannedCosts(costForms);
      setUploadModalOpen(false);
      setScanModalOpen(true);
      notifications.show({ message: `Found ${costForms.length} cost items — review before importing`, color: 'violet' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Cost extraction failed', color: 'red' });
    } finally {
      setAiAnalyzing(false);
    }
  }

  async function handleUpload() {
    if (!uploadFile || !project) return;
    setUploading(true);
    try {
      const FDoc = FIELDS.paDocuments;
      const today = new Date().toISOString().split('T')[0];
      const user = useAuthStore.getState().user;
      const fileType = deriveDocType(uploadFile.name);

      // Determine the document type label for display
      const checkItem = checklistItems.find(c => c.key === uploadDocType);
      const docTypeLabel = checkItem?.label || fileType;

      // Step 1: Create document metadata record
      const fields: Array<[string, string]> = [
        [FDoc.projectId, projRid],
        [FDoc.organizationId, project.organizationId || ''],
        [FDoc.title, uploadFile.name],
        [FDoc.documentType, docTypeLabel],
        [FDoc.uploadedBy, user?.email || ''],
        [FDoc.tags, uploadDocType],
      ];
      const rid = await createRecord(TABLES.paDocuments, fields, token ?? undefined);

      // Step 2: Upload via store (mirrors debris/fileLibrary pattern — stores documentId in notes meta marker)
      await uploadDocumentFile(rid, uploadFile, uploadNotes || '');

      // Step 3: If tagged to a checklist item, create a timeline event
      if (checkItem) {
        await createTimelineEvent({
          projectId: projRid,
          organizationId: project.organizationId || '',
          eventType: 'submission',
          eventDate: today,
          description: `${checkItem.label} uploaded [checklist:${checkItem.key}]`,
          status: 'completed',
          creatorId: user?.email || '',
        });
      }

      setUploadModalOpen(false);
      notifications.show({ title: 'Uploaded', message: `${uploadFile.name} — ${docTypeLabel}`, color: 'green' });
    } catch (e: unknown) {
      notifications.show({ title: 'Upload failed', message: e instanceof Error ? e.message : 'Error', color: 'red' });
    } finally {
      setUploading(false);
    }
  }

  // ── Checklist completion (for non-document items) ──────────

  function openCheckOff(item: ChecklistItem) {
    setCheckingItem(item);
    setCheckNotes('');
    setCheckModalOpen(true);
  }

  async function handleCheckOff() {
    if (!checkingItem || !project) return;
    setCheckSaving(true);
    try {
      const user = useAuthStore.getState().user;
      const today = new Date().toISOString().split('T')[0];
      await createTimelineEvent({
        projectId: projRid,
        organizationId: project.organizationId || '',
        eventType: checkingItem.category === 'milestone' ? 'milestone' : checkingItem.category === 'review' ? 'approval' : 'submission',
        eventDate: today,
        description: `${checkingItem.label} completed [checklist:${checkingItem.key}]${checkNotes ? ' — ' + checkNotes : ''}`,
        status: 'completed',
        creatorId: user?.email || '',
      });
      await fetchAll();
      setCheckModalOpen(false);
      notifications.show({ title: 'Checked off', message: checkingItem.label, color: 'green' });
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed', color: 'red' });
    } finally {
      setCheckSaving(false);
    }
  }

  // ── Revision request handler ───────────────────────────────

  async function handleRequestRevision() {
    if (!revisionItem || !project) return;
    setRevisionSaving(true);
    try {
      const user = useAuthStore.getState().user;
      const today = new Date().toISOString().split('T')[0];
      await createTimelineEvent({
        projectId: projRid,
        organizationId: project.organizationId || '',
        eventType: 'correspondence',
        eventDate: today,
        description: `[revision:${revisionItem.key}] — ${revisionNote || 'Revision requested'}`,
        status: 'pending',
        creatorId: user?.email || '',
      });
      notifications.show({ color: 'orange', title: 'Revision Requested', message: `"${revisionItem.label}" sent back for revision.` });
      setRevisionItem(null);
      setRevisionNote('');
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed', color: 'red' });
    } finally {
      setRevisionSaving(false);
    }
  }

  // ── AI Cost Extraction handlers ────────────────────────────

  async function handleExtractCosts(doc: { documentName: string; Rid: number; mimeType?: string; tags?: string }) {
    setExtracting(true);
    setExtractSource(doc.documentName);
    try {
      const systemPrompt = `You are a FEMA Public Assistance cost extraction specialist. Extract all cost line items from the uploaded document (invoice, receipt, contract, or estimate).

For each line item, return a JSON array with objects containing:
- description: string (what the item/service is)
- quantity: string (number of units, default "1")
- unit: string (unit of measure: hours, EA, LF, CY, LS, etc.)
- unitCost: string (cost per unit as a number string)
- totalCost: string (total cost as a number string)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (one of: A, B, C, D, E, F — A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Administrative Costs)

Categorize intelligently:
- Labor/wages/salaries → costType: "labor", cefPart: "A"
- Equipment ownership/usage → costType: "equipment", cefPart: "B"
- Materials/supplies purchased → costType: "materials", cefPart: "C"
- Rented equipment → costType: "rented_equip", cefPart: "D"
- Contractor/subcontractor work → costType: "contracts", cefPart: "E"
- Admin/overhead → costType: "other", cefPart: "F"

Return ONLY a JSON array. No markdown, no explanation.`;

      const userMessage = `Extract all cost line items from this document: "${doc.documentName}"

Document type: ${doc.mimeType || 'unknown'}
Tagged as: ${doc.tags || 'untagged'}

Please analyze the document name and any available context to extract realistic cost line items. If this appears to be an invoice, extract each line item. If a contract, extract the major cost categories. If a receipt, extract each purchased item.`;

      const response = await callClaude(
        [{ role: 'user', content: userMessage }],
        { system: systemPrompt, maxTokens: 4096 }
      );

      // Parse JSON response — extract JSON array even if surrounded by text
      let cleaned = response.trim();
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
      if (!arrayMatch) throw new Error('AI did not return a valid cost list');

      const items = JSON.parse(arrayMatch[0]) as Array<{
        description: string; quantity: string; unit: string;
        unitCost: string; totalCost: string; costType: string; cefPart: string;
      }>;

      const costForms: CostFormState[] = items.map(item => ({
        costType: item.costType || 'other',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        cefPart: item.cefPart || '',
        vendorEmployee: '',
        sourceReference: doc.documentName,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Extracted by AI from ${doc.documentName}`,
      }));

      setExtractedCosts(costForms);
      setExtractModalOpen(true);
    } catch (err) {
      console.error('AI extraction failed:', err);
      notifications.show({
        title: 'Extraction failed',
        message: err instanceof Error ? err.message : 'Could not extract costs from this document',
        color: 'red',
      });
    } finally {
      setExtracting(false);
    }
  }

  async function handleImportExtractedCosts() {
    if (extractedCosts.length === 0) return;
    setExtracting(true);
    try {
      let imported = 0;
      for (const cost of extractedCosts) {
        await createProjectCost({
          projectId: projRid,
          ...cost,
        });
        imported++;
      }
      await fetchAll();
      notifications.show({
        title: 'Costs imported',
        message: `${imported} line item${imported !== 1 ? 's' : ''} added from ${extractSource}`,
        color: 'green',
      });
      setExtractModalOpen(false);
      setExtractedCosts([]);
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import costs',
        color: 'red',
      });
    } finally {
      setExtracting(false);
    }
  }

  // ── AI Assist handler ──────────────────────────────────────

  async function handleAiAssist() {
    if (!project) return;
    setAiAssistOpen(true);
    setAiAssistLoading(true);
    setAiAssistResult('');
    try {
      // Build project context for Claude
      const checklistSummary = checklistItems.map(item => {
        const status = checklistStatus.get(item.key);
        return `- [${status?.done ? 'x' : ' '}] ${item.label} (${item.category}${item.required ? ', REQUIRED' : ''})${status?.done && status.date ? ` — completed ${status.date}` : ''}`;
      }).join('\n');

      const costSummary = projCosts.map(c =>
        `- ${COST_TYPE_LABELS[c.costType] || c.costType}: ${c.description} — $${parseFloat(c.totalCost || '0').toLocaleString()} (CEF Part ${c.cefPart || 'N/A'})`
      ).join('\n') || 'No cost line items entered';

      const docList = projDocuments.map(d =>
        `- ${d.documentName} (${d.documentType || 'unknown type'}${d.tags ? `, tagged: ${d.tags}` : ''})`
      ).join('\n') || 'No documents uploaded';

      const timelineList = projTimeline.slice(0, 20).map(e =>
        `- ${e.eventDate}: ${e.description} (${e.eventType}, ${e.status})`
      ).join('\n') || 'No timeline events';

      const systemPrompt = `You are a FEMA Public Assistance project review specialist. Review the project data below and provide actionable guidance. Be specific and practical. Format your response in clear sections with markdown.

Focus on:
1. **Checklist Gaps** — What required items are missing? What should be done next?
2. **Cost Review** — Are costs complete? Any categories missing? Does the total seem reasonable?
3. **Documentation** — What key documents are missing or need attention?
4. **Risk Flags** — Any compliance concerns, deadline issues, or red flags?
5. **Next Steps** — Top 3-5 recommended actions in priority order.

Keep it concise and actionable. Don't repeat information the user already knows — focus on what they should DO.`;

      const userMessage = `Review this PA project and provide guidance:

**Project:** ${project.projectNumber} — ${project.projectTitle}
**Category:** ${project.category || 'Not set'} | **Size:** ${project.projectSize || 'Not set'} | **Phase:** ${project.phase} | **Status:** ${project.status}
**Estimated Cost:** $${parseFloat(project.estimatedCost || '0').toLocaleString()} | **Federal Share:** ${project.federalSharePct || '75'}%
**Work Type:** ${project.workType || 'Not set'}
**DDD:** ${project.damageDescription ? project.damageDescription.substring(0, 500) : 'Not provided'}
**SOW:** ${project.scopeOfWork ? project.scopeOfWork.substring(0, 500) : 'Not provided'}

**Checklist (${checklistDone}/${checklistItems.length} complete, ${checklistRequiredDone}/${checklistRequired} required):**
${checklistSummary}

**Cost Line Items (${projCosts.length} items, total: $${projCosts.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0).toLocaleString()}):**
${costSummary}

**Documents (${projDocuments.length}):**
${docList}

**Recent Timeline (${projTimeline.length} events):**
${timelineList}

**Insurance:** ${projInsurance.length} policy record(s)
**Compliance:** ${projCompliance.length} record(s)`;

      const response = await callClaude(
        [{ role: 'user', content: userMessage }],
        { system: systemPrompt, maxTokens: 2048 }
      );
      setAiAssistResult(response);
    } catch (err) {
      setAiAssistResult(`**Error:** ${err instanceof Error ? err.message : 'AI review failed. Please try again.'}`);
    } finally {
      setAiAssistLoading(false);
    }
  }

  // ── AI Scan Receipt handler ────────────────────────────────

  async function handleScanFile(file: File) {
    setScanning(true);
    setScanSource(file.name);
    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const isImg = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const mediaType = file.type || (isImg ? 'image/jpeg' : 'application/pdf');

      const systemPrompt = `You are a FEMA Public Assistance cost extraction specialist. You are given the ACTUAL image or document of a receipt, invoice, contract, or estimate. Read every line item visible in the document and extract them exactly as shown.

CRITICAL: Extract the REAL numbers and descriptions from the document. Do NOT make up or estimate values. Only include items you can actually see in the document.

For each line item, return a JSON array with objects containing:
- description: string (exactly as shown on the document)
- quantity: string (the quantity shown, default "1")
- unit: string (unit of measure: hours, EA, LF, CY, LS, SF, etc.)
- unitCost: string (unit price as shown, as a number string without $ or commas)
- totalCost: string (line total as shown, as a number string without $ or commas)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Admin)

STEP 1 — DETERMINE DOCUMENT TYPE:
First, identify what kind of document this is:
- CONTRACTOR INVOICE: From a licensed contractor, construction company, or professional services firm billing for WORK PERFORMED. Identified by: contractor license #, "invoice" from a company that did the work, service descriptions, work period dates.
- STORE RECEIPT / MATERIAL PURCHASE: Purchase of physical goods from a retail store, supplier, or distributor. Identified by: store name (Home Depot, Lowe's, Grainger, Amazon, etc.), SKU numbers, "sold to" rather than "bill to", no labor descriptions.
- EQUIPMENT RENTAL: From an equipment rental company (United Rentals, Sunbelt, HERC, etc.). Identified by: rental period, daily/weekly rates, equipment serial numbers.
- PAYROLL / TIMESHEET: Employee wages, timesheets, payroll summaries.
- ESTIMATE / QUOTE: Not yet incurred — proposed costs.

STEP 2 — CATEGORIZE EACH LINE ITEM BASED ON DOCUMENT TYPE:

If this is a CONTRACTOR INVOICE (work done by an outside contractor under contract):
- Categorize each line item by WHAT it actually is:
  - Line items for physical materials the contractor installed (roof panels, windows, drywall, lumber, pipe, concrete, etc.) → costType "materials", cefPart "C"
  - Line items for the contractor's labor/services (installation, remediation, inspection, supervision, management, cleanup, demolition) → costType "contracts", cefPart "E"
  - Line items for equipment the contractor rented → costType "rented_equip", cefPart "D"
  This breakdown matters for FEMA's Cost Estimating Format which separates materials from labor even within contracted work.

If this is a STORE RECEIPT / MATERIAL PURCHASE:
- ALL items → costType "materials", cefPart "C"

If this is an EQUIPMENT RENTAL receipt:
- ALL items → costType "rented_equip", cefPart "D"

If this is PAYROLL / TIMESHEETS:
- ALL items → costType "labor", cefPart "A"

Return ONLY a JSON array. No markdown fences, no explanation, no commentary.`;

      // Build message content blocks for Anthropic vision/document API
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let content: any[];
      if (isImg) {
        content = [
          { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
          { type: 'text', text: 'Read this receipt/invoice image. Extract every line item with the exact amounts shown. Return ONLY a JSON array.' },
        ];
      } else if (isPdf) {
        content = [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
          { type: 'text', text: 'Read this invoice/receipt PDF. Extract every line item with the exact amounts shown. Return ONLY a JSON array.' },
        ];
      } else {
        // Fallback for other file types — send as text description
        content = [
          { type: 'text', text: `Extract cost line items from this document: "${file.name}" (${file.type}). I cannot send the file directly, so please provide a template JSON array with reasonable line items for a document of this type. Return ONLY a JSON array.` },
        ];
      }

      const response = await callClaude(
        [{ role: 'user', content }],
        { system: systemPrompt, maxTokens: 4096 },
      );

      // Parse JSON response — extract JSON array even if surrounded by text
      let cleaned = response.trim();
      // Strip markdown fences
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      // Find the JSON array in the response
      const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
      if (!arrayMatch) {
        throw new Error('AI did not return a valid cost list. Try again or use a clearer image.');
      }

      const items = JSON.parse(arrayMatch[0]) as Array<{
        description: string; quantity: string; unit: string;
        unitCost: string; totalCost: string; costType: string; cefPart: string;
      }>;

      const costForms: CostFormState[] = items.map(item => ({
        costType: item.costType || 'other',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        cefPart: item.cefPart || '',
        vendorEmployee: '',
        sourceReference: file.name,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Scanned by AI from ${file.name}`,
      }));

      if (costForms.length === 0) {
        notifications.show({
          title: 'No costs found',
          message: `AI could not extract any cost line items from "${file.name}"`,
          color: 'yellow',
        });
      } else {
        setScannedCosts(costForms);
        setScanModalOpen(true);
      }
    } catch (err) {
      console.error('AI scan failed:', err);
      notifications.show({
        title: 'Scan failed',
        message: err instanceof Error ? err.message : 'Could not read this document',
        color: 'red',
      });
    } finally {
      setScanning(false);
    }
  }

  async function handleImportScannedCosts() {
    if (scannedCosts.length === 0) return;
    setScanning(true);
    try {
      let imported = 0;
      for (const cost of scannedCosts) {
        await createProjectCost({ projectId: projRid, ...cost });
        imported++;
      }
      await fetchAll();
      notifications.show({
        title: 'Costs imported',
        message: `${imported} line item${imported !== 1 ? 's' : ''} added from ${scanSource}`,
        color: 'green',
      });
      setScanModalOpen(false);
      setScannedCosts([]);
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import costs',
        color: 'red',
      });
    } finally {
      setScanning(false);
    }
  }

  // ── Cost modal handlers ────────────────────────────────────

  function openAddCost() {
    setEditingCost(null);
    setCostForm(emptyCostForm());
    setCostModalOpen(true);
  }

  function openEditCost(c: PAProjectCost) {
    setEditingCost(c);
    setCostForm({
      costType: c.costType, description: c.description,
      quantity: c.quantity, unit: c.unit,
      unitCost: c.unitCost, totalCost: c.totalCost,
      cefPart: c.cefPart, vendorEmployee: c.linkedRecordId || '',
      sourceReference: c.sourceReference,
      estimatedOrActual: c.estimatedOrActual || 'estimated',
      eligible: c.eligible || 'true', notes: c.notes,
    });
    setCostModalOpen(true);
  }

  async function handleSaveCost() {
    if (!costForm.costType) {
      notifications.show({ message: 'Cost type is required', color: 'yellow' });
      return;
    }
    setCostSaving(true);
    try {
      const data: Partial<PAProjectCost> = {
        projectId: projRid,
        costType: costForm.costType,
        description: costForm.description,
        quantity: costForm.quantity,
        unit: costForm.unit,
        unitCost: costForm.unitCost,
        totalCost: costForm.totalCost,
        cefPart: costForm.cefPart,
        linkedRecordId: costForm.vendorEmployee,
        sourceReference: costForm.sourceReference,
        estimatedOrActual: costForm.estimatedOrActual,
        eligible: costForm.eligible,
        notes: costForm.notes,
      };
      if (editingCost) {
        await updateProjectCost(editingCost.Rid, data);
        notifications.show({ message: 'Cost line updated', color: 'green' });
      } else {
        await createProjectCost(data);
        notifications.show({ message: 'Cost line added', color: 'green' });
      }
      setCostModalOpen(false);

      // Auto-update project estimatedCost from cost line items
      // Read fresh from store after fetchAll (closure has stale data)
      try {
        await fetchAll();
        const freshCosts = usePAGrantsStore.getState().projectCosts.filter(c => c.projectId === projRid);
        const total = freshCosts.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0);
        const actTotal = freshCosts.filter(c => c.estimatedOrActual === 'actual').reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0);
        if (project && total > 0) {
          await updateProject(project.Rid, {
            estimatedCost: String(total),
            ...(actTotal > 0 ? { actualCost: String(actTotal) } : {}),
          });
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setCostSaving(false);
    }
  }

  async function handleDeleteCost(c: PAProjectCost) {
    if (!confirm(`Delete cost line "${c.description || c.costType}"?`)) return;
    try {
      await deleteProjectCost(c.Rid);
      notifications.show({ message: 'Cost line deleted', color: 'gray' });
      // Recalculate project estimated cost from fresh store data
      try {
        await fetchAll();
        const remaining = usePAGrantsStore.getState().projectCosts.filter(cc => cc.projectId === projRid);
        const total = remaining.reduce((s, cc) => s + (parseFloat(cc.totalCost) || 0), 0);
        if (project) await updateProject(project.Rid, { estimatedCost: String(total) });
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    }
  }

  // ── Timesheet import handler ────────────────────────────────

  async function handleTimesheetImport() {
    if (timesheetSelected.size === 0 || !project) return;
    setTimesheetImporting(true);
    try {
      const selected = laborRecords.filter(r => timesheetSelected.has(r.Rid));
      let imported = 0;
      for (const lr of selected) {
        // Link labor record to this project
        await updateRecord(TABLES.paForceAccountLabor, lr.Rid,
          [[FIELDS.paForceAccountLabor.projectId, projRid]], undefined);
        // Use approved hours/cost if available, fall back to logged
        const approvedReg = lr.approvedRegHours ? parseFloat(lr.approvedRegHours) : num(lr.hoursRegular);
        const approvedOT = lr.approvedOTHours ? parseFloat(lr.approvedOTHours) : num(lr.hoursOvertime);
        const totalHrs = approvedReg + approvedOT;
        const rate = num(lr.rateRegular);
        const costAmount = lr.approvedCost ? lr.approvedCost : (lr.totalCost || String(num(lr.laborCost) + num(lr.fringeCost)));
        // Create a matching cost line item
        await createProjectCost({
          projectId: projRid,
          costType: 'labor',
          description: `Force Account Labor: ${lr.employeeName}${lr.activityDescription ? ' — ' + lr.activityDescription : ''}`,
          quantity: String(totalHrs),
          unit: 'HR',
          unitCost: String(rate),
          totalCost: costAmount,
          estimatedOrActual: 'actual',
          eligible: 'true',
          cefPart: 'A',
          linkedRecordId: String(lr.Rid),
        });
        imported++;
      }
      await fetchLabor();
      await fetchProjectCosts();
      // Recalculate project estimated cost
      try {
        const allCosts = usePAGrantsStore.getState().projectCosts.filter(cc => cc.projectId === projRid);
        const total = allCosts.reduce((s, cc) => s + (parseFloat(cc.totalCost) || 0), 0);
        await updateProject(project.Rid, { estimatedCost: String(total) });
      } catch { /* best effort */ }
      notifications.show({
        title: 'Timesheets Imported',
        message: `Imported ${imported} time ${imported === 1 ? 'entry' : 'entries'} totaling ${fmt(selected.reduce((s, r) => s + num(r.totalCost), 0))}`,
        color: 'green',
      });
      setTimesheetImportOpen(false);
      setTimesheetSelected(new Set());
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'red' });
    } finally {
      setTimesheetImporting(false);
    }
  }

  // ── Insurance modal handlers ───────────────────────────────

  function openAddInsurance() {
    setEditingIns(null);
    setInsForm(emptyInsForm());
    setInsModalOpen(true);
  }

  function openEditInsurance(ins: { Rid: number; policyNumber?: string; carrier?: string; policyType?: string; coverageAmount?: string; deductible?: string; policyEffectiveDate?: string; policyExpirationDate?: string; claimFiled?: string; claimNumber?: string; claimDate?: string; settlementAmount?: string; settlementDate?: string; denialReceived?: string; denialReason?: string; obtainMaintainRequired?: string; obtainMaintainCommitted?: string; femaDeductionAmount?: string }) {
    setEditingIns(ins);
    setInsForm({
      policyNumber: ins.policyNumber || '', carrier: ins.carrier || '',
      policyType: ins.policyType || '', coverageAmount: ins.coverageAmount || '',
      deductible: ins.deductible || '',
      policyEffectiveDate: ins.policyEffectiveDate || '',
      policyExpirationDate: ins.policyExpirationDate || '',
      claimFiled: ins.claimFiled || 'no', claimNumber: ins.claimNumber || '',
      claimDate: ins.claimDate || '',
      settlementAmount: ins.settlementAmount || '',
      settlementDate: ins.settlementDate || '',
      denialReceived: ins.denialReceived || 'no', denialReason: ins.denialReason || '',
      obtainMaintainRequired: ins.obtainMaintainRequired || 'no',
      obtainMaintainCommitted: ins.obtainMaintainCommitted || 'no',
      femaDeductionAmount: ins.femaDeductionAmount || '',
    });
    setInsModalOpen(true);
  }

  async function handleSaveInsurance() {
    setInsSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FI = FIELDS.paInsurance;
      const fields: Array<[string, string]> = [];
      fields.push([FI.projectId, projRid]);
      if (org) fields.push([FI.organizationId, String(org.Rid)]);
      if (insForm.policyNumber) fields.push([FI.policyNumber, insForm.policyNumber]);
      if (insForm.carrier) fields.push([FI.carrier, insForm.carrier]);
      if (insForm.policyType) fields.push([FI.policyType, insForm.policyType]);
      if (insForm.coverageAmount) fields.push([FI.coverageAmount, insForm.coverageAmount]);
      if (insForm.deductible) fields.push([FI.deductible, insForm.deductible]);
      if (insForm.policyEffectiveDate) fields.push([FI.policyEffectiveDate, insForm.policyEffectiveDate]);
      if (insForm.policyExpirationDate) fields.push([FI.policyExpirationDate, insForm.policyExpirationDate]);
      if (insForm.claimFiled) fields.push([FI.claimFiled, insForm.claimFiled]);
      if (insForm.claimNumber) fields.push([FI.claimNumber, insForm.claimNumber]);
      if (insForm.claimDate) fields.push([FI.claimDate, insForm.claimDate]);
      // Always push settlement and deduction amounts (even if empty/zero) to ensure updates clear old values
      fields.push([FI.settlementAmount, insForm.settlementAmount || '0']);
      if (insForm.settlementDate) fields.push([FI.settlementDate, insForm.settlementDate]);
      if (insForm.denialReceived) fields.push([FI.denialReceived, insForm.denialReceived]);
      if (insForm.denialReason) fields.push([FI.denialReason, insForm.denialReason]);
      if (insForm.obtainMaintainRequired) fields.push([FI.obtainMaintainRequired, insForm.obtainMaintainRequired]);
      if (insForm.obtainMaintainCommitted) fields.push([FI.obtainMaintainCommitted, insForm.obtainMaintainCommitted]);
      fields.push([FI.femaDeductionAmount, insForm.femaDeductionAmount || '0']);

      if (editingIns) {
        await updateRecord(TABLES.paInsurance, editingIns.Rid, fields, token);
        notifications.show({ message: 'Insurance record updated', color: 'green' });
      } else {
        await createRecord(TABLES.paInsurance, fields, token);
        notifications.show({ message: 'Insurance record created', color: 'green' });
      }
      setInsModalOpen(false);
      await fetchAll();

      // Auto-update project insuranceDeduction from sum of all settlement amounts
      // Re-read from store AFTER fetchAll to get fresh data (closure holds stale value)
      try {
        const freshInsurance = usePAGrantsStore.getState().insurance;
        const allIns = freshInsurance.filter(i => i.projectId === projRid);
        const totalDeduction = allIns.reduce((sum, i) => {
          return sum + (parseFloat((i as any).settlementAmount || '0') || 0);
        }, 0);
        if (project) {
          await updateProject(project.Rid, { insuranceDeduction: String(totalDeduction) });
          // Re-fetch so the UI updates the deduction display immediately
          await fetchAll();
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setInsSaving(false);
    }
  }

  async function handleDeleteInsurance(rid: number) {
    if (!confirm('Delete this insurance record?')) return;
    try {
      const token = useAuthStore.getState().token ?? undefined;
      await deleteRecord(TABLES.paInsurance, rid, token);
      notifications.show({ message: 'Insurance record deleted', color: 'gray' });
      await fetchAll();
      // Recalculate insurance deduction after delete
      try {
        const freshInsurance = usePAGrantsStore.getState().insurance;
        const allIns = freshInsurance.filter(i => i.projectId === projRid);
        const totalDeduction = allIns.reduce((sum, i) => {
          return sum + (parseFloat((i as any).settlementAmount || '0') || 0);
        }, 0);
        if (project) {
          await updateProject(project.Rid, { insuranceDeduction: String(totalDeduction) });
          await fetchAll();
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    }
  }

  // ── Compliance modal handlers ──────────────────────────────

  function openEditCompliance() {
    const comp = projCompliance[0];
    if (comp) {
      setCompForm({
        nepaRequired: comp.nepaRequired || '', nepaLevel: comp.nepaLevel || '',
        nepaStatus: comp.nepaStatus || '', nepaCompletionDate: comp.nepaCompletionDate || '',
        section106Required: comp.section106Required || '', section106Status: comp.section106Status || '',
        historicProperty: comp.historicProperty || '', shpoConsultation: comp.shpoConsultation || '',
        tribalConsultation: comp.tribalConsultation || '',
        inFloodplain: comp.inFloodplain || '', floodZone: comp.floodZone || '',
        floodInsuranceRequired: comp.floodInsuranceRequired || '', eo11988Compliance: comp.eo11988Compliance || '',
        coastalBarrierArea: comp.coastalBarrierArea || '', coastalZone: comp.coastalZone || '',
        esaConsultationRequired: comp.esaConsultationRequired || '', hazmatPresent: comp.hazmatPresent || '',
      });
    } else {
      setCompForm(emptyCompForm());
    }
    setCompModalOpen(true);
  }

  async function handleSaveCompliance() {
    setCompSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FC = FIELDS.paCompliance;
      const fields: Array<[string, string]> = [];
      fields.push([FC.projectId, projRid]);
      if (org) fields.push([FC.organizationId, String(org.Rid)]);
      if (compForm.nepaRequired) fields.push([FC.nepaRequired, compForm.nepaRequired]);
      if (compForm.nepaLevel) fields.push([FC.nepaLevel, compForm.nepaLevel]);
      if (compForm.nepaStatus) fields.push([FC.nepaStatus, compForm.nepaStatus]);
      if (compForm.nepaCompletionDate) fields.push([FC.nepaCompletionDate, compForm.nepaCompletionDate]);
      if (compForm.section106Required) fields.push([FC.section106Required, compForm.section106Required]);
      if (compForm.section106Status) fields.push([FC.section106Status, compForm.section106Status]);
      if (compForm.historicProperty) fields.push([FC.historicProperty, compForm.historicProperty]);
      if (compForm.shpoConsultation) fields.push([FC.shpoConsultation, compForm.shpoConsultation]);
      if (compForm.tribalConsultation) fields.push([FC.tribalConsultation, compForm.tribalConsultation]);
      if (compForm.inFloodplain) fields.push([FC.inFloodplain, compForm.inFloodplain]);
      if (compForm.floodZone) fields.push([FC.floodZone, compForm.floodZone]);
      if (compForm.floodInsuranceRequired) fields.push([FC.floodInsuranceRequired, compForm.floodInsuranceRequired]);
      if (compForm.eo11988Compliance) fields.push([FC.eo11988Compliance, compForm.eo11988Compliance]);
      if (compForm.coastalBarrierArea) fields.push([FC.coastalBarrierArea, compForm.coastalBarrierArea]);
      if (compForm.coastalZone) fields.push([FC.coastalZone, compForm.coastalZone]);
      if (compForm.esaConsultationRequired) fields.push([FC.esaConsultationRequired, compForm.esaConsultationRequired]);
      if (compForm.hazmatPresent) fields.push([FC.hazmatPresent, compForm.hazmatPresent]);

      const existing = projCompliance[0];
      if (existing) {
        await updateRecord(TABLES.paCompliance, existing.Rid, fields, token);
        notifications.show({ message: 'Compliance updated', color: 'green' });
      } else {
        await createRecord(TABLES.paCompliance, fields, token);
        notifications.show({ message: 'Compliance record created', color: 'green' });
      }
      setCompModalOpen(false);
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setCompSaving(false);
    }
  }

  // ── SCQ save handler ────────────────────────────────────────

  async function handleSaveScq() {
    setScqSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FC = FIELDS.paCompliance;
      const fields: Array<[string, string]> = [];
      fields.push([FC.projectId, projRid]);
      if (org) fields.push([FC.organizationId, String(org.Rid)]);

      // Map SCQ answers back to compliance fields
      for (const q of SCQ_QUESTIONS) {
        if (q.compField) {
          const answer = scqAnswers[q.key]?.answer || '';
          fields.push([FC[q.compField], answer]);
        }
      }

      // Store comments as JSON in complianceNotes
      const comments: Record<string, string> = {};
      for (const q of SCQ_QUESTIONS) {
        if (scqAnswers[q.key]?.comment) {
          comments[q.key] = scqAnswers[q.key].comment;
        }
      }
      fields.push([FC.complianceNotes, JSON.stringify(comments)]);

      const existing = projCompliance[0];
      if (existing) {
        await updateRecord(TABLES.paCompliance, existing.Rid, fields, token);
      } else {
        await createRecord(TABLES.paCompliance, fields, token);
      }
      notifications.show({ message: 'Special Considerations saved', color: 'green' });
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setScqSaving(false);
    }
  }

  // ── Timeline event modal handlers ──────────────────────────

  function openAddEvent() {
    setEventForm(emptyEventForm());
    setEventModalOpen(true);
  }

  async function handleSaveEvent() {
    if (!eventForm.description?.trim()) {
      notifications.show({ message: 'Event description is required', color: 'yellow' });
      return;
    }
    setEventSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FT = FIELDS.paTimelineEvents;
      const fields: Array<[string, string]> = [];
      fields.push([FT.projectId, projRid]);
      if (org) fields.push([FT.organizationId, String(org.Rid)]);
      if (eventForm.eventType) fields.push([FT.eventType, eventForm.eventType]);
      if (eventForm.eventDate) fields.push([FT.eventDate, eventForm.eventDate]);
      if (eventForm.dueDate) fields.push([FT.dueDate, eventForm.dueDate]);
      if (eventForm.description) fields.push([FT.description, eventForm.description]);
      if (eventForm.status) fields.push([FT.status, eventForm.status]);

      await createRecord(TABLES.paTimelineEvents, fields, token);
      notifications.show({ message: 'Event added', color: 'green' });
      setEventModalOpen(false);
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setEventSaving(false);
    }
  }

  // ── Notes handler ──────────────────────────────────────────

  async function handleAddNote() {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FN = FIELDS.paNotes;
      const userName = useAuthStore.getState().user?.fullName || 'Unknown';
      const fields: Array<[string, string]> = [];
      fields.push([FN.parentType, 'project']);
      fields.push([FN.parentId, projRid]);
      fields.push([FN.content, noteText.trim()]);
      fields.push([FN.creatorId, userName]);
      fields.push([FN.createdDate, new Date().toISOString().split('T')[0]]);
      if (org) fields.push([FN.organizationId, String(org.Rid)]);

      await createRecord(TABLES.paNotes, fields, token);
      notifications.show({ message: 'Note added', color: 'green' });
      setNoteText('');
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to add note', color: 'red' });
    } finally {
      setNoteSaving(false);
    }
  }

  // ── Task handlers ──────────────────────────────────────────

  function openAddTask() {
    setTaskForm({ description: '', dueDate: '', status: 'pending' });
    setTaskModalOpen(true);
  }

  async function handleSaveTask() {
    if (!taskForm.description.trim()) {
      notifications.show({ message: 'Description is required', color: 'yellow' });
      return;
    }
    setTaskSaving(true);
    try {
      await createTimelineEvent({
        projectId: projRid,
        organizationId: org ? String(org.Rid) : '',
        eventType: 'task',
        description: taskForm.description,
        dueDate: taskForm.dueDate,
        eventDate: new Date().toISOString().split('T')[0],
        status: taskForm.status,
      });
      notifications.show({ message: 'Task created', color: 'green' });
      setTaskModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setTaskSaving(false);
    }
  }

  async function handleToggleTask(t: { Rid: number; status: string }) {
    const isComplete = t.status === 'completed';
    try {
      await updateTimelineEvent(t.Rid, {
        status: isComplete ? 'pending' : 'completed',
        completedDate: isComplete ? '' : new Date().toISOString().split('T')[0],
      });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'red' });
    }
  }

  async function handleDeleteTask(rid: number) {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTimelineEvent(rid);
      notifications.show({ message: 'Task deleted', color: 'gray' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    }
  }

  // ── Edit Project modal handlers ────────────────────────────

  function openEditProject() {
    if (!project) return;
    setEditProjForm({
      projectNumber: project.projectNumber,
      projectTitle: project.projectTitle,
      category: project.category,
      projectSize: project.projectSize,
      workType: project.workType,
      projectAddress: project.projectAddress,
      damageDescription: project.damageDescription,
      scopeOfWork: project.scopeOfWork,
      costNarrative: project.costNarrative,
      estimatedCost: project.estimatedCost,
      actualCost: project.actualCost,
      federalSharePct: project.federalSharePct || '75',
      workStartDate: project.workStartDate,
      deadline: project.deadline,
      completionPctEstimate: project.completionPctEstimate,
      assignedSpecialist: project.assignedSpecialist,
      status: project.status,
      phase: project.phase,
      notes: project.notes,
    });
    setEditProjTab('general');
    setEditProjModalOpen(true);
  }

  async function handleSaveProject() {
    if (!project) return;
    if (!editProjForm.projectTitle?.trim()) {
      notifications.show({ message: 'Project title is required', color: 'yellow' });
      return;
    }
    setEditProjSaving(true);
    try {
      await updateProject(project.Rid, editProjForm);
      notifications.show({ message: 'Project updated', color: 'green' });
      setEditProjModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setEditProjSaving(false);
    }
  }

  // ── Loading / not found ────────────────────────────────────

  if (loadingProjects && projects.length === 0) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!project) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Project not found</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(orgId ? `/pagrants/pa/applicants/${orgId}` : '/pagrants/pa/projects')}>
            Back
          </Button>
        </Stack>
      </Center>
    );
  }

  // Org ownership guard: applicant roles can only view their own org's projects
  if (isApplicantRole && myOrgIds.length > 0 && !myOrgIds.includes(String(project.organizationId))) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Access Denied</Text>
          <Text size="sm" c="dimmed">This project belongs to another organization.</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/pagrants/pa/projects')}>
            Back to My Projects
          </Button>
        </Stack>
      </Center>
    );
  }

  // ── Computed values ────────────────────────────────────────

  const estimated = num(project.estimatedCost);
  const fedPct = num(project.federalSharePct) || 75;
  const federalShare = num(project.federalShareAmount) || estimated * (fedPct / 100);
  const applicantShare = num(project.applicantShareAmount) || estimated - federalShare;
  const insuranceDed = num(project.insuranceDeduction);
  const netEligible = num(project.netEligibleCost) || estimated - insuranceDed;
  const obligated = num(project.obligatedAmount);
  const completionPct = num(project.completionPctEstimate);

  const orgName = org?.orgName || `Org #${orgId}`;
  const disasterName = disaster?.disasterName || project.disasterId || 'N/A';
  const cat = (project.category || 'A') as DamageCategory;

  // ── Compliance status helper ───────────────────────────────

  function complianceIcon(value: string | undefined) {
    const v = (value || '').toLowerCase();
    if (v === 'completed' || v === 'yes' || v === 'true') {
      return <ThemeIcon size={20} radius="xl" color="green" variant="filled"><IconCheck size={12} /></ThemeIcon>;
    }
    if (v === 'in_progress' || v === 'in progress' || v === 'pending') {
      return <ThemeIcon size={20} radius="xl" color="yellow" variant="filled"><IconClock size={12} /></ThemeIcon>;
    }
    if (v === 'not_required' || v === 'n/a') {
      return <ThemeIcon size={20} radius="xl" color="gray" variant="light"><IconCheck size={12} /></ThemeIcon>;
    }
    return <ThemeIcon size={20} radius="xl" color="gray" variant="light"><IconClock size={12} /></ThemeIcon>;
  }

  // ── File icon helper ───────────────────────────────────────

  function openDocViewer(doc: any) {
    setViewDoc(doc);
    setDocFileAvailable(null);
  }

  function fileIcon(mime: string) {
    if (mime.includes('pdf')) return <IconFileTypePdf size={18} />;
    if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
      return <IconFileSpreadsheet size={18} />;
    return <IconFile size={18} />;
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Stack gap="md">
      {/* ── HEADER ─────────────────────────────────────────── */}
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Breadcrumbs items={isApplicantRole ? [
              { label: 'Disaster Grants', to: '/pagrants' },
              { label: 'My Projects', to: '/pagrants/pa/projects' },
              { label: project.projectNumber || 'Project' },
            ] : [
              { label: 'Disaster Grants', to: '/pagrants' },
              { label: 'PA', to: '/pagrants/pa' },
              { label: orgName, to: `/pagrants/pa/applicants/${orgId}` },
              { label: project.projectNumber || 'Project' },
            ]} />

            <Group gap="sm" align="center">
              <Title order={3}>
                {project.projectNumber || 'No Number'} &mdash; {project.projectTitle || 'Untitled Project'}
              </Title>
              {!isApplicantViewer && (
                <Tooltip label="Edit Project">
                  <ActionIcon variant="subtle" color="gray" size="sm" onClick={openEditProject}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Badge
                style={{ backgroundColor: DAMAGE_CATEGORY_COLORS[cat] || '#6B7280' }}
                c="white" size="lg"
              >
                Cat {cat}
              </Badge>
              <Badge variant="light" size="lg" color="blue">
                {(project.projectSize || 'small').toUpperCase()}
              </Badge>
              <Badge variant="light" size="lg" color={
                project.status === 'completed' ? 'green' :
                project.status === 'appealed' ? 'red' :
                project.status === 'obligated' ? 'teal' : 'gray'
              }>
                {PROJECT_STATUS_LABELS[project.status as ProjectStatus] || project.status}
              </Badge>
            </Group>

            <Text size="sm" c="dimmed">
              {orgName} | {disasterName}{!isApplicantRole && ` | Assigned: ${project.assignedSpecialist || 'Unassigned'}`}
            </Text>
          </Stack>

          <Group gap="xs">
            <Button variant="light" leftSection={<IconFileDescription size={16} />}>
              Generate FEMA Form
            </Button>
            {!isApplicantViewer && (
              <Button variant="light" color="violet" leftSection={<IconRobot size={16} />}
                loading={aiAssistLoading} onClick={handleAiAssist}>
                AI Assist
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      {/* ── COST SUMMARY BAR ───────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
        {[
          { label: 'Estimated Cost', value: estimated, color: undefined },
          { label: `Federal Share (${fedPct}%)`, value: federalShare, color: undefined },
          { label: 'Applicant Share', value: applicantShare, color: undefined },
          { label: 'Insurance Deduction', value: insuranceDed, color: insuranceDed > 0 ? 'red' : undefined },
          { label: 'Net Eligible', value: netEligible, color: 'green' },
          { label: 'Obligated', value: obligated, color: undefined },
        ].map((item) => (
          <Paper key={item.label} p="sm" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{item.label}</Text>
            <Text size="lg" fw={700} c={item.color}>{fmt(item.value)}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* ── TABS ───────────────────────────────────────────── */}
      <Tabs defaultValue="checklist">
        {/* Hidden file input — lives outside all tab panels so it's always mounted */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.zip,.rar"
          onChange={onFileSelect}
        />

        <Tabs.List>
          <Tabs.Tab value="checklist" leftSection={<IconListCheck size={14} />}>
            Checklist ({checklistDone}/{checklistItems.length})
          </Tabs.Tab>
          <Tabs.Tab value="scope">Scope &amp; Damage</Tabs.Tab>
          <Tabs.Tab value="costs">Costs</Tabs.Tab>
          <Tabs.Tab value="insurance">Insurance</Tabs.Tab>
          <Tabs.Tab value="compliance">Compliance</Tabs.Tab>
          <Tabs.Tab value="documents">Documents ({projDocuments.length})</Tabs.Tab>
          <Tabs.Tab value="tasks">Tasks ({projTasks.length})</Tabs.Tab>
          <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
          <Tabs.Tab value="photos">Photos</Tabs.Tab>
          <Tabs.Tab value="contracts">Contracts</Tabs.Tab>
          <Tabs.Tab value="notes">Notes</Tabs.Tab>
          <Tabs.Tab value="extensions">Extensions</Tabs.Tab>
          <Tabs.Tab value="reimbursements">Reimbursements</Tabs.Tab>
          <Tabs.Tab value="mitigation">406 Mitigation</Tabs.Tab>
        </Tabs.List>

        {/* ════════════════════════════════════════════════════
            TAB 0: PROJECT CHECKLIST
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="checklist" pt="md">
          <Stack gap="md">
            {/* Progress summary */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="blue"><IconListCheck size={18} /></ThemeIcon>
                  <Text fw={600}>Project Checklist</Text>
                </Group>
                <Group gap="sm">
                  <Badge color="green" variant="light">{checklistDone} / {checklistItems.length} complete</Badge>
                  <Badge color={checklistRequiredDone === checklistRequired ? 'green' : 'orange'} variant="light">
                    {checklistRequiredDone} / {checklistRequired} required
                  </Badge>
                </Group>
              </Group>
              <Progress
                value={(checklistDone / checklistItems.length) * 100}
                size="lg" radius="xl" color="green"
                style={{ background: 'var(--mantine-color-gray-2)' }}
              />
            </Paper>

            {/* Grouped checklist items */}
            {(['document', 'form', 'review', 'milestone'] as const).map(cat => {
              const items = checklistItems.filter(c => c.category === cat);
              const catDone = items.filter(c => checklistStatus.get(c.key)?.done).length;
              return (
                <Paper key={cat} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Group gap="xs">
                      <Badge color={CHECKLIST_CATEGORY_COLORS[cat]} variant="filled" size="sm">
                        {CHECKLIST_CATEGORY_LABELS[cat]}s
                      </Badge>
                      <Text size="xs" c="dimmed">{catDone}/{items.length}</Text>
                    </Group>
                  </Group>
                  <Stack gap={4}>
                    {items.map(item => {
                      const st = checklistStatus.get(item.key);
                      const done = st?.done ?? false;
                      const hasRevision = st?.revision ?? false;
                      return (
                        <Group key={item.key} justify="space-between" py={6}
                          style={{
                            borderBottom: '1px solid var(--mantine-color-gray-1)',
                            opacity: done ? 0.7 : 1,
                            background: hasRevision ? 'var(--mantine-color-orange-0)' : undefined,
                            padding: hasRevision ? '8px' : undefined,
                            borderRadius: hasRevision ? 4 : undefined,
                          }}>
                          <Group gap="sm" style={{ flex: 1 }}>
                            {done ? (
                              <ThemeIcon size="sm" variant="filled" color="green" radius="xl">
                                <IconCheck size={12} />
                              </ThemeIcon>
                            ) : hasRevision ? (
                              <ThemeIcon size="sm" variant="filled" color="orange" radius="xl">
                                <IconArrowLeft size={12} />
                              </ThemeIcon>
                            ) : (
                              <ThemeIcon size="sm" variant="light" color="gray" radius="xl">
                                <IconCircle size={12} />
                              </ThemeIcon>
                            )}
                            <Stack gap={0}>
                              <Group gap={6}>
                                <Text size="sm" fw={500} td={done ? 'line-through' : undefined}>
                                  {item.label}
                                </Text>
                                {item.required && !done && !hasRevision && (
                                  <Badge size="xs" variant="outline" color="red">Required</Badge>
                                )}
                                {hasRevision && (
                                  <Badge size="xs" variant="filled" color="orange">Revision Needed</Badge>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">{item.description}</Text>
                              {hasRevision && st?.revisionNote && (
                                <Paper p="xs" mt={4} style={{ background: 'var(--mantine-color-orange-1)', borderRadius: 4 }}>
                                  <Text size="xs" fw={500} c="orange.8">Reviewer feedback:</Text>
                                  <Text size="xs">{st.revisionNote}</Text>
                                  <Text size="xs" c="dimmed" mt={2}>{st.revisionBy} — {st.revisionDate}</Text>
                                </Paper>
                              )}
                              {done && st?.date && (
                                <Text size="xs" c="green">
                                  Completed {st.date}{st.by ? ` by ${st.by}` : ''}
                                </Text>
                              )}
                            </Stack>
                          </Group>
                          <Group gap={4}>
                            {!done && (cat === 'document' ? (
                              <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                                onClick={() => {
                                  setUploadDocType(item.key);
                                  fileInputRef.current?.click();
                                }}>
                                {hasRevision ? 'Re-upload' : 'Upload'}
                              </Button>
                            ) : !done && (
                              <Button size="xs" variant="light" color="green"
                                leftSection={<IconCheckbox size={12} />}
                                onClick={() => openCheckOff(item)}>
                                Mark Done
                              </Button>
                            ))}
                            {done && !isApplicantRole && (
                              <Button size="xs" variant="light" color="orange"
                                leftSection={<IconArrowLeft size={12} />}
                                onClick={() => { setRevisionItem(item); setRevisionNote(''); }}>
                                Request Revision
                              </Button>
                            )}
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 1: SCOPE & DAMAGE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="scope" pt="md">
          <Stack gap="md">
            {/* Damage Description + Scope of Work */}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Paper p="md" withBorder>
                <Text fw={600} mb="xs">Damage Description</Text>
                <div className="sow-scroll-box" style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--mantine-color-gray-2)', borderRadius: 4, padding: 12, background: 'var(--mantine-color-gray-0)', fontSize: 13, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(project.damageDescription || 'No damage description provided.') }} />
              </Paper>

              <Paper p="md" withBorder>
                <Text fw={600} mb="xs">Scope of Work</Text>
                <div className="sow-scroll-box" style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--mantine-color-gray-2)', borderRadius: 4, padding: 12, background: 'var(--mantine-color-gray-0)', fontSize: 13, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: renderSimpleMarkdown(project.scopeOfWork || 'No scope of work defined.') }} />
              </Paper>
            </SimpleGrid>

            {/* Associated Damage Sites */}
            <Paper p="md" withBorder>
              <Text fw={600} mb="sm">Associated Damage Sites</Text>
              {projDamageItems.length === 0 ? (
                <Text size="sm" c="dimmed">No damage sites associated with this project.</Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Facility</Table.Th>
                      <Table.Th>Damage</Table.Th>
                      <Table.Th>Pre-Disaster Condition</Table.Th>
                      <Table.Th>Est. Cost</Table.Th>
                      <Table.Th>Inspected</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projDamageItems.map(d => (
                      <Table.Tr key={d.Rid}>
                        <Table.Td>{d.facilityName || 'N/A'}</Table.Td>
                        <Table.Td style={{ maxWidth: 300 }}>
                          <Text size="sm" lineClamp={2}>{d.damageDescription || '-'}</Text>
                        </Table.Td>
                        <Table.Td>{d.preDisasterCondition || '-'}</Table.Td>
                        <Table.Td>{fmt(num(d.estimatedCost))}</Table.Td>
                        <Table.Td>
                          <Badge
                            color={d.siteInspected === 'yes' || d.siteInspected === 'true' ? 'green' : 'gray'}
                            variant="light" size="sm"
                          >
                            {d.siteInspected === 'yes' || d.siteInspected === 'true' ? 'Yes' : 'No'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>

            {/* Schedule & Milestones */}
            <Paper p="md" withBorder>
              <Text fw={600} mb="sm">Schedule &amp; Milestones</Text>
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" tt="uppercase">Work Start</Text>
                  <Text fw={500}>{project.workStartDate || 'TBD'}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" tt="uppercase">Deadline</Text>
                  <Text fw={500}>{project.deadline || 'TBD'}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" tt="uppercase">Completion</Text>
                  <Progress value={completionPct} size="lg" mt={4} />
                  <Text size="xs" ta="right">{completionPct}%</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed" tt="uppercase">Repair / Replace</Text>
                  <Text fw={500}>{project.repairOrReplace || 'N/A'}</Text>
                </Stack>
              </SimpleGrid>
            </Paper>

            {/* Hazard Mitigation (conditional) */}
            {(project.hazardMitigationIncluded || '').toLowerCase() === 'yes' && (
              <Paper p="md" withBorder>
                <Text fw={600} mb="sm">Hazard Mitigation</Text>
                <SimpleGrid cols={{ base: 1, md: 3 }}>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Description</Text>
                    <Text size="sm">{project.hazardMitigationDescription || '-'}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Cost</Text>
                    <Text fw={500}>{fmt(num(project.hazardMitigationCost))}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">BCA</Text>
                    <Text fw={500}>{project.hazardMitigationBca || 'N/A'}</Text>
                  </Stack>
                </SimpleGrid>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 2: COSTS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="costs" pt="md">
          <Stack gap="md">
            {/* Cost summary by type */}
            <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
              {(['contracts', 'labor', 'equipment', 'materials', 'rented_equip', 'donated'] as const).map(t => (
                <Paper key={t} p="sm" withBorder>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    {COST_TYPE_LABELS[t]}
                  </Text>
                  <Text size="lg" fw={700}>{fmt(costsByType[t] || 0)}</Text>
                </Paper>
              ))}
            </SimpleGrid>

            {/* Filter buttons */}
            <Group gap="xs">
              <Button
                size="xs" variant={costFilter === null ? 'filled' : 'light'}
                onClick={() => setCostFilter(null)}
              >
                All
              </Button>
              <div style={{ marginLeft: 'auto' }}>
                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => {
                  exportToCSV(`costs-${project?.projectNumber || 'project'}.csv`,
                    ['Type', 'Description', 'Qty', 'Unit', 'Unit Cost', 'Total', 'Est/Actual', 'Eligible'],
                    projCosts.map(c => [c.costType, c.description, c.quantity, c.unit, c.unitCost, c.totalCost, c.estimatedOrActual, c.eligible])
                  );
                }}>Export</Button>
              </div>
              {Object.entries(COST_TYPE_LABELS).map(([key, label]) => (
                <Button
                  key={key} size="xs"
                  variant={costFilter === key ? 'filled' : 'light'}
                  color={COST_TYPE_COLORS[key]}
                  onClick={() => setCostFilter(costFilter === key ? null : key)}
                >
                  {label}
                </Button>
              ))}
            </Group>

            {/* Costs table */}
            <Paper p="md" withBorder>
              {projCosts.length === 0 ? (
                <Text size="sm" c="dimmed">No cost records{costFilter ? ` for ${COST_TYPE_LABELS[costFilter] || costFilter}` : ''}.</Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Notes</Table.Th>
                      <Table.Th>Qty</Table.Th>
                      <Table.Th>Unit Cost</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>Est/Actual</Table.Th>
                      <Table.Th>Eligible</Table.Th>
                      <Table.Th w={70}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projCosts.map(c => (
                      <Table.Tr key={c.Rid}>
                        <Table.Td>
                          <Badge color={COST_TYPE_COLORS[c.costType] || 'gray'} variant="light" size="sm">
                            {COST_TYPE_LABELS[c.costType] || c.costType}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{c.description || '-'}</Table.Td>
                        <Table.Td>{c.notes || '-'}</Table.Td>
                        <Table.Td>{c.quantity || '-'}</Table.Td>
                        <Table.Td>{fmt(num(c.unitCost))}</Table.Td>
                        <Table.Td fw={700}>{fmt(num(c.totalCost))}</Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={c.estimatedOrActual === 'actual' ? 'green' : 'gray'}>
                            {c.estimatedOrActual || 'est'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={c.eligible === 'false' ? 'red' : 'green'}>
                            {c.eligible === 'false' ? 'No' : 'Yes'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon variant="subtle" size="sm" onClick={() => openEditCost(c)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="red" onClick={() => handleDeleteCost(c)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}

              <Group mt="md">
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddCost}>
                  Add Cost Line
                </Button>
                <input
                  type="file"
                  ref={scanInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleScanFile(f);
                    e.target.value = '';
                  }}
                />
                <Button size="xs" variant="light" color="violet"
                  leftSection={<IconScan size={14} />}
                  loading={scanning}
                  onClick={() => scanInputRef.current?.click()}>
                  Scan Receipt / Invoice
                </Button>
                <Button size="xs" variant="light" color="violet"
                  leftSection={<IconClock size={14} />}
                  onClick={() => { setTsCatFilter(project?.category || null); setTimesheetImportOpen(true); }}>
                  Import from Timesheets
                </Button>
              </Group>
            </Paper>

            {/* Assigned timesheets summary */}
            {projLabor.length > 0 && (
              <Paper p="md" withBorder mt="md" style={{ borderLeft: '3px solid var(--mantine-color-violet-5)' }}>
                <Text fw={600} mb="sm">Force Account Labor ({projLabor.length} timesheets)</Text>
                <Table striped fz="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Employee</Table.Th>
                      <Table.Th>Activity</Table.Th>
                      <Table.Th>Hours</Table.Th>
                      <Table.Th>Total Cost</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projLabor.map(lr => (
                      <Table.Tr key={lr.Rid}>
                        <Table.Td>{lr.workDate || '-'}</Table.Td>
                        <Table.Td>{lr.employeeName || '-'}</Table.Td>
                        <Table.Td>{lr.activityDescription || '-'}</Table.Td>
                        <Table.Td>{num(lr.hoursRegular) + num(lr.hoursOvertime)}</Table.Td>
                        <Table.Td>{fmt(num(lr.totalCost))}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="sm" c="dimmed" mt="xs">
                  Total: {projLabor.reduce((s, r) => s + num(r.hoursRegular) + num(r.hoursOvertime), 0)} hrs
                  {' '}&mdash; {fmt(projLabor.reduce((s, r) => s + num(r.totalCost), 0))}
                </Text>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 3: INSURANCE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="insurance" pt="md">
          <Stack gap="md">
            {projInsurance.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No insurance records for this project.</Text>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddInsurance}>
                    Add Insurance Record
                  </Button>
                </Stack>
              </Paper>
            ) : (
              projInsurance.map(ins => (
                <Paper key={ins.Rid} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Text fw={600}>Policy: {ins.policyNumber || 'N/A'}</Text>
                    <Group gap="xs">
                      <Badge variant="light" color={
                        ins.status === 'settled' ? 'green' :
                        ins.status === 'denied' ? 'red' :
                        ins.status === 'claim_filed' ? 'blue' : 'gray'
                      }>
                        {(ins.status || 'pending').replace(/_/g, ' ')}
                      </Badge>
                      <ActionIcon variant="subtle" size="sm" onClick={() => openEditInsurance(ins)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm" color="red" onClick={() => handleDeleteInsurance(ins.Rid)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Carrier</Text>
                      <Text fw={500}>{ins.carrier || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Policy Type</Text>
                      <Text fw={500}>{ins.policyType || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Coverage</Text>
                      <Text fw={500}>{fmt(num(ins.coverageAmount))}</Text>
                    </Stack>
                    <Tooltip label="The deductible is not subtracted from eligible costs — it remains reimbursable per PAPPG Ch. 4.VII" multiline w={260} withArrow>
                      <Stack gap={2} style={{ cursor: 'help' }}>
                        <Text size="xs" c="dimmed" tt="uppercase">Deductible</Text>
                        <Text fw={500}>{fmt(num(ins.deductible))}</Text>
                      </Stack>
                    </Tooltip>
                  </SimpleGrid>

                  <Divider mb="sm" label="Claim Tracking" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Claim Filed</Text>
                      <Text fw={500}>{ins.claimFiled || 'No'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Claim Number</Text>
                      <Text fw={500}>{ins.claimNumber || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Settlement Amount</Text>
                      <Text fw={500}>{fmt(num(ins.settlementAmount))}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Settlement Date</Text>
                      <Text fw={500}>{ins.settlementDate || '-'}</Text>
                    </Stack>
                  </SimpleGrid>

                  <Divider mb="sm" label="Obtain & Maintain" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">O&amp;M Required</Text>
                      <Text fw={500}>{ins.obtainMaintainRequired || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">O&amp;M Committed</Text>
                      <Text fw={500}>{ins.obtainMaintainCommitted || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">FEMA Deduction</Text>
                      <Text fw={500} c={num(ins.femaDeductionAmount) > 0 ? 'red' : undefined}>
                        {fmt(num(ins.femaDeductionAmount))}
                      </Text>
                    </Stack>
                  </SimpleGrid>
                </Paper>
              ))
            )}
            {projInsurance.length > 0 && (
              <>
                <Paper p="sm" radius="sm" bg="blue.0" style={{ border: '1px solid var(--mantine-color-blue-2)' }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size="sm" variant="light" color="blue" radius="xl"><IconShieldCheck size={12} /></ThemeIcon>
                    <Text size="xs" c="blue.8">
                      <Text span fw={600}>Insurance deduction note:</Text> Only the settlement amount (what insurance paid) is deducted from eligible project costs.
                      The policy deductible is <Text span fw={600}>not</Text> subtracted — it remains eligible for FEMA reimbursement per PAPPG Chapter 4.VII.
                    </Text>
                  </Group>
                </Paper>
                <Group>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddInsurance}>
                    Add Insurance Record
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 4: COMPLIANCE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="compliance" pt="md">
          <Stack gap="md">

            {/* ── Special Consideration Questions (FEMA 009-0-120) ── */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb={scqExpanded ? 'sm' : 0}
                style={{ cursor: 'pointer' }}
                onClick={() => setScqExpanded(v => !v)}>
                <Group gap="xs">
                  <IconShieldCheck size={18} color="var(--mantine-color-orange-6)" />
                  <Text fw={600} size="sm">Special Consideration Questions</Text>
                  <Badge size="xs" variant="light" color="gray">FEMA 009-0-120</Badge>
                  {(() => {
                    const answered = SCQ_QUESTIONS.filter(q => scqAnswers[q.key]?.answer).length;
                    return <Badge size="xs" variant="light" color={answered === 8 ? 'green' : 'orange'}>{answered}/8 answered</Badge>;
                  })()}
                </Group>
                {scqExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              </Group>

              {scqExpanded && (
                <Stack gap="sm">
                  <Text size="xs" c="dimmed">
                    Complete these questions for each project per FEMA requirements. Select Yes, No, or Unsure and add comments where applicable.
                  </Text>
                  {SCQ_QUESTIONS.map(q => (
                    <Paper key={q.key} p="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
                      <Text size="sm" mb="xs">{q.text}</Text>
                      <Group gap="sm" align="flex-start">
                        <SegmentedControl
                          size="xs"
                          data={[
                            { value: '', label: '—' },
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Unsure' },
                          ]}
                          value={scqAnswers[q.key]?.answer || ''}
                          onChange={v => setScqAnswers(prev => ({
                            ...prev,
                            [q.key]: { ...prev[q.key], answer: v, comment: prev[q.key]?.comment || '' },
                          }))}
                          color={scqAnswers[q.key]?.answer === 'yes' ? 'orange' : scqAnswers[q.key]?.answer === 'no' ? 'green' : 'gray'}
                        />
                        <TextInput
                          size="xs"
                          placeholder="Comments..."
                          style={{ flex: 1 }}
                          value={scqAnswers[q.key]?.comment || ''}
                          onChange={e => setScqAnswers(prev => ({
                            ...prev,
                            [q.key]: { ...prev[q.key], answer: prev[q.key]?.answer || '', comment: e.target.value },
                          }))}
                        />
                      </Group>
                    </Paper>
                  ))}
                  <Group justify="flex-end">
                    <Button size="xs" loading={scqSaving} onClick={handleSaveScq}
                      leftSection={<IconCheck size={14} />}>
                      Save Special Considerations
                    </Button>
                  </Group>
                </Stack>
              )}
            </Paper>

            {/* ── Detailed Compliance Record ── */}
            {projCompliance.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No detailed compliance record for this project.</Text>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openEditCompliance}>
                    Add Compliance Record
                  </Button>
                </Stack>
              </Paper>
            ) : (
              projCompliance.map(comp => (
                <Stack key={comp.Rid} gap="md">
                  <SimpleGrid cols={{ base: 1, md: 2 }}>
                    {/* NEPA/Environmental */}
                    <Paper p="md" withBorder>
                      <Text fw={600} mb="sm">NEPA / Environmental Review</Text>
                      <Stack gap="xs">
                        <Group gap="xs">
                          {complianceIcon(comp.nepaRequired)}
                          <Text size="sm">NEPA Required: <strong>{comp.nepaRequired || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.nepaLevel ? 'completed' : 'not_started')}
                          <Text size="sm">NEPA Level: <strong>{comp.nepaLevel || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.nepaStatus)}
                          <Text size="sm">NEPA Status: <strong>{comp.nepaStatus || '-'}</strong></Text>
                        </Group>
                        {comp.nepaCompletionDate && (
                          <Text size="xs" c="dimmed" ml={36}>Completion Date: {comp.nepaCompletionDate}</Text>
                        )}
                        <Divider my={4} />
                        <Group gap="xs">
                          {complianceIcon(comp.esaConsultationRequired)}
                          <Text size="sm">ESA Consultation: <strong>{comp.esaConsultationRequired || '-'}</strong></Text>
                        </Group>
                        {comp.esaStatus && (
                          <Text size="xs" c="dimmed" ml={36}>ESA Status: {comp.esaStatus}</Text>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.protectedSpeciesPresent)}
                          <Text size="sm">Protected Species: <strong>{comp.protectedSpeciesPresent || '-'}</strong></Text>
                        </Group>
                        <Divider my={4} />
                        <Group gap="xs">
                          {complianceIcon(comp.wetlandsImpacted)}
                          <Text size="sm">Wetlands Impacted: <strong>{comp.wetlandsImpacted || '-'}</strong></Text>
                        </Group>
                        {comp.wetlandsPermitRequired && (
                          <Text size="xs" c="dimmed" ml={36}>Permit Required: {comp.wetlandsPermitRequired}</Text>
                        )}
                        <Divider my={4} />
                        <Group gap="xs">
                          {complianceIcon(comp.hazmatPresent === 'Yes' ? 'in_progress' : comp.hazmatPresent)}
                          <Text size="sm">Hazmat Present: <strong>{comp.hazmatPresent || '-'}</strong></Text>
                        </Group>
                        {comp.hazmatType && (
                          <Text size="xs" c="dimmed" ml={36}>Type: {comp.hazmatType}</Text>
                        )}
                        {comp.hazmatMitigationRequired && (
                          <Text size="xs" c="dimmed" ml={36}>Mitigation Required: {comp.hazmatMitigationRequired}</Text>
                        )}
                      </Stack>
                    </Paper>

                    {/* Section 106/Historic */}
                    <Paper p="md" withBorder>
                      <Text fw={600} mb="sm">Section 106 / Historic Preservation</Text>
                      <Stack gap="xs">
                        <Group gap="xs">
                          {complianceIcon(comp.section106Required)}
                          <Text size="sm">Section 106 Required: <strong>{comp.section106Required || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.section106Status)}
                          <Text size="sm">Status: <strong>{comp.section106Status || '-'}</strong></Text>
                        </Group>
                        {comp.section106CompletionDate && (
                          <Text size="xs" c="dimmed" ml={36}>Completion Date: {comp.section106CompletionDate}</Text>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.historicProperty)}
                          <Text size="sm">Historic Property: <strong>{comp.historicProperty || '-'}</strong></Text>
                        </Group>
                        {comp.historicPropertyAge && (
                          <Text size="xs" c="dimmed" ml={36}>Property Age: {comp.historicPropertyAge} years</Text>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.shpoConsultation)}
                          <Text size="sm">SHPO Consultation: <strong>{comp.shpoConsultation || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.tribalConsultation)}
                          <Text size="sm">Tribal Consultation: <strong>{comp.tribalConsultation || '-'}</strong></Text>
                        </Group>
                        {comp.indianSacredSites && (
                          <Group gap="xs">
                            {complianceIcon(comp.indianSacredSites)}
                            <Text size="sm">Indian Sacred Sites: <strong>{comp.indianSacredSites}</strong></Text>
                          </Group>
                        )}
                      </Stack>
                    </Paper>

                    {/* Floodplain/Flood Insurance */}
                    <Paper p="md" withBorder>
                      <Text fw={600} mb="sm">Floodplain / Flood Insurance</Text>
                      <Stack gap="xs">
                        <Group gap="xs">
                          {complianceIcon(comp.inFloodplain)}
                          <Text size="sm">In Floodplain: <strong>{comp.inFloodplain || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.floodZone ? 'completed' : 'not_started')}
                          <Text size="sm">Flood Zone: <strong>{comp.floodZone || '-'}</strong></Text>
                        </Group>
                        {comp.baseFloodElevation && (
                          <Text size="xs" c="dimmed" ml={36}>Base Flood Elevation: {comp.baseFloodElevation}</Text>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.floodInsuranceRequired)}
                          <Text size="sm">Flood Insurance Required: <strong>{comp.floodInsuranceRequired || '-'}</strong></Text>
                        </Group>
                        {comp.floodInsuranceObtained && (
                          <Group gap="xs">
                            {complianceIcon(comp.floodInsuranceObtained)}
                            <Text size="sm">Flood Insurance Obtained: <strong>{comp.floodInsuranceObtained}</strong></Text>
                          </Group>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.eo11988Compliance)}
                          <Text size="sm">EO 11988 Compliance: <strong>{comp.eo11988Compliance || '-'}</strong></Text>
                        </Group>
                      </Stack>
                    </Paper>

                    {/* Coastal/Barrier Resources */}
                    <Paper p="md" withBorder>
                      <Text fw={600} mb="sm">Coastal / Barrier Resources</Text>
                      <Stack gap="xs">
                        <Group gap="xs">
                          {complianceIcon(comp.coastalBarrierArea)}
                          <Text size="sm">Coastal Barrier Area: <strong>{comp.coastalBarrierArea || '-'}</strong></Text>
                        </Group>
                        <Group gap="xs">
                          {complianceIcon(comp.coastalZone)}
                          <Text size="sm">Coastal Zone: <strong>{comp.coastalZone || '-'}</strong></Text>
                        </Group>
                        {comp.coastalZoneConsistency && (
                          <Text size="xs" c="dimmed" ml={36}>Consistency: {comp.coastalZoneConsistency}</Text>
                        )}
                        <Group gap="xs">
                          {complianceIcon(comp.esaConsultationRequired)}
                          <Text size="sm">ESA Consultation Required: <strong>{comp.esaConsultationRequired || '-'}</strong></Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </SimpleGrid>

                  {/* Overall Status */}
                  <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <Text size="sm" fw={600}>Overall EHP Status:</Text>
                        <Badge size="sm" color={
                          comp.overallComplianceStatus === 'completed' ? 'green' :
                          comp.overallComplianceStatus === 'in_progress' ? 'yellow' :
                          comp.overallComplianceStatus === 'not_started' ? 'gray' : 'gray'
                        }>
                          {(comp.overallComplianceStatus || 'Not Set').replace(/_/g, ' ')}
                        </Badge>
                      </Group>
                      {comp.ehpReviewerId && (
                        <Text size="xs" c="dimmed">EHP Reviewer: {comp.ehpReviewerId}</Text>
                      )}
                    </Group>
                  </Paper>

                  <Group>
                    <Button size="sm" variant="light" leftSection={<IconEdit size={14} />} onClick={openEditCompliance}>
                      Edit Compliance
                    </Button>
                  </Group>
                </Stack>
              ))
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 5: DOCUMENTS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="documents" pt="md">
          <Stack gap="md">
            {/* Upload zone */}
            <Paper
              p="lg" withBorder
              style={{
                border: '2px dashed var(--mantine-color-blue-3)',
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              <Stack align="center" gap="xs">
                {uploading ? <Loader size={32} /> : <IconUpload size={32} color="var(--mantine-color-blue-5)" />}
                <Text size="sm" c="dimmed">
                  {uploading ? 'Uploading...' : 'Click to upload — you\'ll tag the document type next'}
                </Text>
                <Text size="xs" c="dimmed">PDF, DOCX, XLSX, images, ZIP accepted</Text>
              </Stack>
            </Paper>

            {projDocuments.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center">No documents for this project yet.</Text>
            ) : (
              Object.entries(docsByType).map(([type, docs]) => (
                <Paper key={type} p="md" withBorder>
                  <Text fw={600} mb="sm">{type}</Text>
                  <Stack gap="xs">
                    {docs.map(doc => {
                      const checkItem = checklistItems.find(c => c.key === doc.tags);
                      return (
                        <Group key={doc.Rid} justify="space-between" p="xs"
                          style={{ borderRadius: 6, background: 'var(--mantine-color-gray-0)', cursor: 'pointer' }}
                          onClick={() => openDocViewer(doc)}>
                          <Group gap="sm">
                            {fileIcon(doc.mimeType || '')}
                            <Stack gap={0}>
                              <Group gap={4}>
                                <Text size="sm" fw={500} c="blue">{doc.documentName || 'Unnamed'}</Text>
                              </Group>
                              <Text size="xs" c="dimmed">
                                {doc.uploadDate || '-'} {doc.uploadedBy ? `by ${doc.uploadedBy}` : ''}
                                {doc.fileSize ? ` | ${(parseInt(doc.fileSize) / 1024).toFixed(0)} KB` : ''}
                              </Text>
                            </Stack>
                          </Group>
                          <Group gap="xs">
                            {checkItem && (
                              <Badge size="xs" variant="light" color="green" leftSection={<IconCheck size={10} />}>
                                {checkItem.label}
                              </Badge>
                            )}
                            {doc.tags && !checkItem && (
                              <Badge size="xs" variant="light">{doc.tags}</Badge>
                            )}
                            <ActionIcon variant="subtle" size="sm" color="violet"
                              loading={extracting}
                              title="Extract costs with AI"
                              onClick={async (e) => { e.stopPropagation(); await handleExtractCosts(doc); }}>
                              <IconRobot size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="blue"
                              title="Edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDocViewer(doc);
                                setEditingDoc(true);
                                setEditDocForm({
                                  title: doc.documentName || '',
                                  documentType: doc.documentType || '',
                                  tags: doc.tags || '',
                                  notes: doc.notes || '', // already stripped by store mapper
                                });
                              }}>
                              <IconPencil size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="red"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await deleteDocument(doc.Rid);
                                  notifications.show({ message: 'Document deleted', color: 'gray' });
                                } catch { /* ignore */ }
                              }}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Tabs.Panel>

        {/* Document Viewer Modal */}
        <Modal opened={!!viewDoc} onClose={() => { setViewDoc(null); setEditingDoc(false); setDocFileAvailable(null); }}
          title={editingDoc ? 'Edit Document' : (viewDoc?.documentName || 'Document')} size="lg">
          {viewDoc && (
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Type</Text>
                  <Text size="sm" fw={500}>{viewDoc.documentType || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Uploaded</Text>
                  <Text size="sm">{viewDoc.uploadDate || '—'} {viewDoc.uploadedBy ? `by ${viewDoc.uploadedBy}` : ''}</Text>
                </div>
              </SimpleGrid>
              {viewDoc.description && (
                <div>
                  <Text size="xs" c="dimmed">Description</Text>
                  <Text size="sm">{viewDoc.description}</Text>
                </div>
              )}
              {viewDoc.tags && (
                <div>
                  <Text size="xs" c="dimmed">Tagged</Text>
                  <Badge size="sm" variant="light">{checklistItems.find(c => c.key === viewDoc.tags)?.label || viewDoc.tags}</Badge>
                </div>
              )}

              {/* Content display */}
              {(() => {
                const raw = viewDoc.notes || '';
                // Separate user notes from legacy embedded file data
                let userNotes = '';
                let legacyFileData = '';
                if (raw.includes('|||FILE|||')) {
                  userNotes = raw.split('|||FILE|||')[0].trim();
                  legacyFileData = raw.split('|||FILE|||')[1] || '';
                } else if (!raw.startsWith('data:')) {
                  userNotes = raw;
                } else {
                  legacyFileData = raw; // entire notes is a data URL (old format)
                }

                const isLegacyImage = legacyFileData.startsWith('data:image');
                const isLegacyPdf = legacyFileData.startsWith('data:application/pdf');
                const isLegacyDataUrl = legacyFileData.startsWith('data:');

                const downloadLegacyFile = (data: string, name: string) => {
                  const a = document.createElement('a');
                  a.href = data;
                  a.download = name;
                  a.click();
                };

                return (
                  <>
                    {userNotes && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Notes</Text>
                        <Text size="sm" mb="sm">{userNotes}</Text>
                      </div>
                    )}
                    {/* Native V2 attachment — download via store (mirrors debris/fileLibrary pattern) */}
                    {viewDoc.fileId && (
                      <Button size="xs" variant="light" leftSection={<IconDownload size={12} />}
                        onClick={async () => {
                          try {
                            const url = await getDocumentDownloadUrl(viewDoc.fileId);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } catch (err: any) {
                            notifications.show({
                              title: 'Download unavailable',
                              message: err?.message === 'FILE_UNAVAILABLE'
                                ? 'File not available — it may not have been uploaded or the upload did not complete.'
                                : 'Could not retrieve download link.',
                              color: 'orange', autoClose: 6000,
                            });
                          }
                        }}>
                        Download {viewDoc.documentName || 'File'}
                      </Button>
                    )}
                    {/* Legacy base64 embedded in notes field */}
                    {!viewDoc.fileId && isLegacyImage && (
                      <div>
                        <img src={legacyFileData} alt={viewDoc.documentName} style={{ maxWidth: '100%', borderRadius: 8 }} />
                        <Button size="xs" variant="light" mt="sm" leftSection={<IconDownload size={12} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'image')}>Download</Button>
                      </div>
                    )}
                    {!viewDoc.fileId && isLegacyPdf && (
                      <div>
                        <iframe src={legacyFileData} title={viewDoc.documentName}
                          style={{ width: '100%', height: 500, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }} />
                        <Button size="xs" variant="light" mt="sm" leftSection={<IconDownload size={12} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'document.pdf')}>Download PDF</Button>
                        {legacyFileData.length >= 65000 && (
                          <Text size="xs" c="orange" mt={4}>This PDF may be truncated due to storage limits.</Text>
                        )}
                      </div>
                    )}
                    {!viewDoc.fileId && isLegacyDataUrl && !isLegacyImage && !isLegacyPdf && (
                      <div>
                        <Button size="sm" variant="light" leftSection={<IconDownload size={14} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'file')}>Download File</Button>
                      </div>
                    )}
                    {!viewDoc.fileId && !legacyFileData && !userNotes && (
                      <Paper p="md" bg="gray.0" radius="sm" ta="center">
                        <IconFile size={32} color="var(--mantine-color-gray-4)" style={{ marginBottom: 8 }} />
                        <Text size="sm" c="dimmed">No file attached.</Text>
                      </Paper>
                    )}
                  </>
                );
              })()}

              {editingDoc ? (
                <Stack gap="sm" mt="sm">
                  <input type="file" ref={editDocFileRef} style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.zip"
                    onChange={e => setEditDocFile(e.target.files?.[0] || null)} />
                  <TextInput label="Title" size="sm"
                    value={editDocForm.title}
                    onChange={e => setEditDocForm(f => ({ ...f, title: e.target.value }))} />
                  <Select label="Document Type" size="sm"
                    data={DOC_TYPE_OPTIONS}
                    value={editDocForm.documentType}
                    onChange={v => setEditDocForm(f => ({ ...f, documentType: v || '' }))} />
                  <TextInput label="Tags / Checklist Link" size="sm" placeholder="optional"
                    value={editDocForm.tags}
                    onChange={e => setEditDocForm(f => ({ ...f, tags: e.target.value }))} />
                  <Textarea label="Notes" size="sm" rows={2}
                    value={editDocForm.notes}
                    onChange={e => setEditDocForm(f => ({ ...f, notes: e.target.value }))} />
                  {/* File swap */}
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Replace / Add File</Text>
                    <Group gap="xs">
                      <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                        onClick={() => editDocFileRef.current?.click()}>
                        {editDocFile ? 'Change File' : 'Attach File'}
                      </Button>
                      {editDocFile && (
                        <Text size="xs" c="green">{editDocFile.name}</Text>
                      )}
                    </Group>
                  </div>
                  <Group justify="flex-end" gap="xs">
                    <Button variant="default" size="sm" onClick={() => { setEditingDoc(false); setEditDocFile(null); }}>Cancel</Button>
                    <Button size="sm" loading={uploading} onClick={async () => {
                      try {
                        let newNotes = editDocForm.notes;
                        let newTitle = editDocForm.title;
                        if (editDocFile) {
                          // Upload new file via store (same pattern as debris/fileLibrary)
                          if (!newTitle) newTitle = editDocFile.name;
                          await updateDocument(viewDoc.Rid, {
                            title: newTitle,
                            documentType: editDocForm.documentType,
                            tags: editDocForm.tags,
                          });
                          await uploadDocumentFile(viewDoc.Rid, editDocFile, editDocForm.notes);
                        } else {
                          // Preserve existing fileId as meta marker — viewDoc.notes is already stripped
                          const metaPrefix = viewDoc.fileId ? `|||DOCID|||${viewDoc.fileId}|||ATTACHID|||${viewDoc.fileId}|||` : '';
                          newNotes = metaPrefix + editDocForm.notes;
                          await updateDocument(viewDoc.Rid, {
                            title: newTitle,
                            documentType: editDocForm.documentType,
                            tags: editDocForm.tags,
                            notes: newNotes,
                          });
                        }
                        notifications.show({ message: 'Document updated', color: 'green' });
                        setEditingDoc(false);
                        setEditDocFile(null);
                        setViewDoc(null);
                      } catch {
                        notifications.show({ message: 'Failed to save', color: 'red' });
                      }
                    }}>Save</Button>
                  </Group>
                </Stack>
              ) : (
                <Group justify="flex-end" mt="xs">
                  <Button variant="light" size="sm" leftSection={<IconPencil size={14} />}
                    onClick={() => {
                      setEditingDoc(true);
                      setEditDocForm({
                        title: viewDoc?.documentName || '',
                        documentType: viewDoc?.documentType || '',
                        tags: viewDoc?.tags || '',
                        notes: viewDoc?.notes || '', // already stripped by store mapper
                      });
                    }}>Edit</Button>
                  <Button variant="default" size="sm" onClick={() => setViewDoc(null)}>Close</Button>
                </Group>
              )}
            </Stack>
          )}
        </Modal>

        {/* ════════════════════════════════════════════════════
            TAB 6: TASKS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="tasks" pt="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Tasks &amp; Deadlines</Text>
              <Group gap="xs">
                <Popover opened={templatePopoverOpen} onChange={setTemplatePopoverOpen} position="bottom-end" width={320}>
                  <Popover.Target>
                    <Button size="xs" variant="light" color="violet"
                      leftSection={<IconListCheck size={14} />}
                      onClick={() => setTemplatePopoverOpen(o => !o)}>
                      Load Template
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>Task Templates</Text>
                      <Text size="xs" c="dimmed">Select a template to add pre-built tasks to this project:</Text>
                      {PA_TASK_TEMPLATES.map(tmpl => (
                        <Paper key={tmpl.name} p="xs" withBorder style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            setTemplatePopoverOpen(false);
                            const today = new Date();
                            let created = 0;
                            for (const task of tmpl.tasks) {
                              const dueDate = new Date(today.getTime() + task.daysFromStart * 86400000);
                              const dueDateStr = dueDate.toISOString().split('T')[0];
                              try {
                                await createTimelineEvent({
                                  projectId: projRid,
                                  organizationId: project?.organizationId || (org ? String(org.Rid) : ''),
                                  eventType: 'task',
                                  eventDate: today.toISOString().split('T')[0],
                                  dueDate: dueDateStr,
                                  description: task.description,
                                  status: 'pending',
                                });
                                created++;
                              } catch { /* continue */ }
                            }
                            notifications.show({ message: `${created} tasks added from "${tmpl.name}"`, color: 'green' });
                          }}>
                          <Text size="sm" fw={500}>{tmpl.name}</Text>
                          <Text size="xs" c="dimmed">{tmpl.tasks.length} tasks</Text>
                        </Paper>
                      ))}
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddTask}>
                  Add Task
                </Button>
              </Group>
            </Group>

            {projTasks.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No tasks for this project yet.</Text>
                </Stack>
              </Paper>
            ) : (
              <Stack gap="xs">
                {projTasks.map(t => {
                  const overdue = t.dueDate && t.dueDate < todayStr && t.status !== 'completed';
                  const dueSoon = t.dueDate && !overdue && t.status !== 'completed'
                    && t.dueDate <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
                  const borderColor = overdue ? '#DC2626' : t.status === 'completed' ? '#059669' : dueSoon ? '#D97706' : '#16A34A';
                  return (
                    <Paper key={t.Rid} p="sm" withBorder
                      style={{ borderLeft: `3px solid ${borderColor}` }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                          <ActionIcon
                            variant="subtle"
                            color={t.status === 'completed' ? 'green' : 'gray'}
                            onClick={() => handleToggleTask(t)}
                          >
                            {t.status === 'completed' ? <IconCircleCheck size={20} /> : <IconCircle size={20} />}
                          </ActionIcon>
                          <Stack gap={2} style={{ flex: 1 }}>
                            <Popover width={350} position="bottom-start" withArrow>
                              <Popover.Target>
                                <Text size="sm" fw={500} style={{ cursor: 'pointer' }}
                                  td={t.status === 'completed' ? 'line-through' : undefined}
                                  c={t.status === 'completed' ? 'dimmed' : undefined}
                                >
                                  {t.description}
                                </Text>
                              </Popover.Target>
                              <Popover.Dropdown>
                                <Textarea size="xs" label="Task description" autosize minRows={2}
                                  defaultValue={t.description}
                                  onBlur={async (e) => {
                                    const val = e.target.value.trim();
                                    if (val && val !== t.description) {
                                      try { await updateTimelineEvent(t.Rid, { description: val }); }
                                      catch { /* ignore */ }
                                    }
                                  }}
                                />
                              </Popover.Dropdown>
                            </Popover>
                            <Group gap="xs">
                              <Popover width={200} position="bottom" withArrow>
                                <Popover.Target>
                                  <Badge size="xs" variant="light" style={{ cursor: 'pointer' }}
                                    color={t.dueDate ? (overdue ? 'red' : dueSoon ? 'yellow' : 'green') : 'gray'}
                                    leftSection={<IconClock size={10} />}
                                  >
                                    {t.dueDate ? `Due ${t.dueDate}` : 'Set due date'}
                                  </Badge>
                                </Popover.Target>
                                <Popover.Dropdown>
                                  <TextInput size="xs" type="date" label="Due Date"
                                    defaultValue={t.dueDate || ''}
                                    onChange={async (e) => {
                                      const val = e.target.value;
                                      try {
                                        await updateTimelineEvent(t.Rid, { dueDate: val });
                                      } catch { /* ignore */ }
                                    }}
                                  />
                                </Popover.Dropdown>
                              </Popover>
                              <Badge size="xs" variant="light"
                                color={t.status === 'completed' ? 'green' : t.status === 'in_progress' ? 'yellow' : 'gray'}
                              >
                                {(t.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                              </Badge>
                            </Group>
                          </Stack>
                        </Group>
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDeleteTask(t.Rid)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 7: TIMELINE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="timeline" pt="md">
          <Stack gap="md">
            {projTimeline.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No timeline events for this project.</Text>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddEvent}>
                    Add Event
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <>
                {/* Visual timeline bar chart */}
                <Paper p="md" withBorder mb="md">
                  <Text size="sm" fw={600} mb="sm">Project Timeline</Text>
                  <div style={{ position: 'relative', padding: '20px 0', minHeight: 80 }}>
                    {/* Horizontal axis line */}
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, background: '#dee2e6' }} />
                    {/* Today marker */}
                    {(() => {
                      const dates = projTimeline.map(e => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate).filter(Boolean).sort();
                      if (dates.length < 2) return null;
                      const minD = new Date(dates[0]).getTime();
                      const maxD = new Date(dates[dates.length - 1]).getTime();
                      const range = maxD - minD || 1;
                      const nowPct = Math.min(100, Math.max(0, ((Date.now() - minD) / range) * 100));
                      return (
                        <>
                          <div style={{ position: 'absolute', left: `${nowPct}%`, top: 0, bottom: 0, width: 2, background: '#fa5252', zIndex: 2 }} />
                          <div style={{ position: 'absolute', left: `${nowPct}%`, top: -18, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 10, color: '#fa5252', fontWeight: 600 }}>Today</div>
                        </>
                      );
                    })()}
                    {/* Event dots — use completedDate for completed items so late completions show actual date */}
                    {(() => {
                      const effectiveDate = (e: typeof projTimeline[number]) =>
                        (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate;
                      const dates = projTimeline.map(e => effectiveDate(e)).filter(Boolean).sort();
                      if (dates.length < 2) return null;
                      const minD = new Date(dates[0]).getTime();
                      const maxD = new Date(dates[dates.length - 1]).getTime();
                      const range = maxD - minD || 1;
                      return projTimeline.map(evt => {
                        const eDate = effectiveDate(evt);
                        if (!eDate) return null;
                        const pct = ((new Date(eDate).getTime() - minD) / range) * 100;
                        const color = evt.status === 'completed' ? '#2f9e44' :
                          evt.status === 'in_progress' ? '#f08c00' :
                          '#adb5bd';
                        const wasLate = evt.status === 'completed' && evt.completedDate && evt.dueDate && evt.completedDate > evt.dueDate;
                        return (
                          <Tooltip key={evt.Rid} multiline w={240}
                            label={<div><Text size="xs" fw={600}>{(evt.eventType || '').replace(/_/g, ' ')}</Text>{evt.dueDate && <Text size="xs">Due: {evt.dueDate}</Text>}{evt.completedDate && <Text size="xs" c="green">Completed: {evt.completedDate}</Text>}{wasLate && <Badge size="xs" color="orange" variant="light">Completed late</Badge>}<Text size="xs" c="dimmed">{evt.description}</Text>{evt.status && <Badge size="xs" mt={2} variant="light" color={evt.status === 'completed' ? 'green' : 'gray'}>{evt.status}</Badge>}</div>}
                            position="top" withArrow
                          >
                            <div
                              style={{
                                position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                                width: 18, height: 18, borderRadius: '50%', background: color,
                                border: '3px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                                cursor: 'pointer', zIndex: 3,
                              }}
                            />
                          </Tooltip>
                        );
                      });
                    })()}
                  </div>
                  {/* Date labels */}
                  {(() => {
                    const dates = projTimeline.map(e => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate).filter(Boolean).sort();
                    if (dates.length < 2) return null;
                    return (
                      <Group justify="space-between" mt={4}>
                        <Text size="xs" c="dimmed">{fmtDate(dates[0])}</Text>
                        <Text size="xs" c="dimmed">{fmtDate(dates[dates.length - 1])}</Text>
                      </Group>
                    );
                  })()}
                </Paper>

                {projTimeline.map((evt, idx) => (
                  <Group key={evt.Rid} gap="md" align="flex-start" wrap="nowrap">
                    {/* Vertical connector */}
                    <Stack align="center" gap={0} style={{ minWidth: 40 }}>
                      <ThemeIcon
                        size={32} radius="xl" variant="light"
                        color={TIMELINE_EVENT_COLORS[evt.eventType as TimelineEventType] || 'gray'}
                      >
                        <IconClock size={16} />
                      </ThemeIcon>
                      {idx < projTimeline.length - 1 && (
                        <div style={{
                          width: 2, height: 40,
                          background: 'var(--mantine-color-gray-3)',
                        }} />
                      )}
                    </Stack>

                    <Paper p="sm" withBorder style={{ flex: 1 }}>
                      <Group justify="space-between" mb={4}>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>{fmtDate(evt.eventDate) || 'No date'}</Text>
                          <Badge
                            size="sm" variant="light"
                            color={TIMELINE_EVENT_COLORS[evt.eventType as TimelineEventType] || 'gray'}
                          >
                            {(evt.eventType || 'event').replace(/_/g, ' ')}
                          </Badge>
                        </Group>
                        {evt.status && (
                          <Badge size="sm" variant="light" color={
                            evt.status === 'completed' ? 'green' :
                            evt.status === 'overdue' ? 'red' : 'gray'
                          }>
                            {(evt.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm">{evt.description || '-'}</Text>
                    </Paper>
                  </Group>
                ))}

                <Group>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddEvent}>
                    Add Event
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: PHOTOS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="photos" pt="md">
          {project && (
            <ProjectPhotos
              projectId={projRid}
              organizationId={project.organizationId || ''}
              documents={projDocuments}
            />
          )}
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: CONTRACTS (FEMA 009-0-126)
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="contracts" pt="md">
          <Contracts projectId={projRid} />
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 8: NOTES
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="notes" pt="md">
          <Stack gap="md">
            {/* Add note area */}
            <Paper p="md" withBorder>
              <Textarea
                placeholder="Add a note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                minRows={3} mb="sm"
              />
              <Group justify="flex-end">
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  disabled={!noteText.trim() || noteSaving}
                  loading={noteSaving}
                  onClick={handleAddNote}>
                  Add Note
                </Button>
              </Group>
            </Paper>

            {projNotes.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center">No notes yet.</Text>
            ) : (
              projNotes.map(note => (
                <Paper key={note.Rid} p="md" withBorder>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{note.content}</Text>
                  <Group mt="xs" gap="xs">
                    <Text size="xs" c="dimmed">{note.creatorId || 'Unknown'}</Text>
                    <Text size="xs" c="dimmed">|</Text>
                    <Text size="xs" c="dimmed">{note.createdDate || '-'}</Text>
                  </Group>
                </Paper>
              ))
            )}
          </Stack>
        </Tabs.Panel>

        {/* ═══════ Extensions Tab ═══════ */}
        <Tabs.Panel value="extensions" pt="md">
          {(() => {
            const projExtensions = timeExtensions.filter(t => String(t.projectId) === String(projectId));
            const teStatusColors: Record<string, string> = { draft: 'gray', submitted: 'blue', grantee_approved: 'green', forwarded_to_fema: 'indigo', approved: 'teal', denied: 'red', revision_needed: 'orange' };
            const approvedMonths = projExtensions
              .filter(t => ['grantee_approved', 'forwarded_to_fema', 'approved'].includes(t.status))
              .reduce((sum, t) => sum + (parseInt(t.extensionMonths) || 0), 0);
            const catLetter = project?.category || '';
            const authorityLimit = ['A', 'B'].includes(catLetter.toUpperCase()) ? 6 : 30;
            return (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group gap="md">
                    <Text fw={600}>Time Extension Requests ({projExtensions.length})</Text>
                    {approvedMonths > 0 && (
                      <Badge size="sm" color={approvedMonths > authorityLimit ? 'orange' : 'blue'} variant="light">
                        {approvedMonths} of {authorityLimit} mo used (Cat {catLetter})
                      </Badge>
                    )}
                  </Group>
                  <Button size="xs" variant="light" leftSection={<IconClock size={14} />} onClick={() => navigate('/pagrants/pa/time-extensions')}>Manage All</Button>
                </Group>
                {projExtensions.length === 0 ? (
                  <Text c="dimmed" size="sm">No time extension requests for this project.</Text>
                ) : (
                  <Paper withBorder>
                    <Table striped highlightOnHover>
                      <Table.Thead><Table.Tr>
                        <Table.Th>Current Deadline</Table.Th><Table.Th>Requested Deadline</Table.Th>
                        <Table.Th>Extension</Table.Th><Table.Th>Status</Table.Th><Table.Th>Date</Table.Th>
                      </Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {projExtensions.map(te => (
                          <Table.Tr key={te.id}>
                            <Table.Td>{te.currentDeadline || '—'}</Table.Td>
                            <Table.Td>{te.requestedNewDeadline || '—'}</Table.Td>
                            <Table.Td>{te.extensionMonths ? `${te.extensionMonths} mo` : '—'}</Table.Td>
                            <Table.Td><Badge color={teStatusColors[te.status] || 'gray'} size="sm">{te.status}</Badge></Table.Td>
                            <Table.Td>{te.requestDate || '—'}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                )}
              </Stack>
            );
          })()}
        </Tabs.Panel>

        {/* ═══════ Reimbursements Tab ═══════ */}
        <Tabs.Panel value="reimbursements" pt="md">
          {(() => {
            const projRFRs = reimbursementRequests.filter(r => String(r.projectId) === String(projectId));
            const rfrStatusColors: Record<string, string> = { draft: 'gray', submitted: 'blue', under_review: 'indigo', approved: 'green', denied: 'red', revision_needed: 'orange', paid: 'teal' };
            const totalRequested = projRFRs.reduce((s, r) => s + (parseFloat(r.requestedAmount) || 0), 0);
            const totalApproved = projRFRs.filter(r => r.status === 'approved' || r.status === 'paid').reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
            const totalPaid = projRFRs.filter(r => r.status === 'paid').reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
            const fmtC = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
            return (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Reimbursement Requests ({projRFRs.length})</Text>
                  <Button size="xs" variant="light" onClick={() => navigate('/pagrants/pa/reimbursements')}>Manage All</Button>
                </Group>
                <SimpleGrid cols={3} spacing="sm">
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Requested</Text><Text fw={700}>{fmtC(totalRequested)}</Text></Paper>
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Approved</Text><Text fw={700} c="green">{fmtC(totalApproved)}</Text></Paper>
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Paid</Text><Text fw={700} c="teal">{fmtC(totalPaid)}</Text></Paper>
                </SimpleGrid>
                {projRFRs.length === 0 ? (
                  <Text c="dimmed" size="sm">No reimbursement requests for this project.</Text>
                ) : (
                  <Paper withBorder>
                    <Table striped highlightOnHover>
                      <Table.Thead><Table.Tr>
                        <Table.Th>RFR #</Table.Th><Table.Th style={{ textAlign: 'right' }}>Requested</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Approved</Table.Th><Table.Th>Status</Table.Th><Table.Th>Date</Table.Th>
                      </Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {projRFRs.map(rfr => (
                          <Table.Tr key={rfr.id}>
                            <Table.Td fw={500}>{rfr.requestNumber || '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{rfr.requestedAmount ? fmtC(parseFloat(rfr.requestedAmount)) : '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{rfr.approvedAmount ? fmtC(parseFloat(rfr.approvedAmount)) : '—'}</Table.Td>
                            <Table.Td><Badge color={rfrStatusColors[rfr.status] || 'gray'} size="sm">{rfr.status}</Badge></Table.Td>
                            <Table.Td>{rfr.submittedDate || rfr.requestDate || '—'}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                )}
              </Stack>
            );
          })()}
        </Tabs.Panel>

        {/* ── 406 Mitigation ────────────────────────────── */}
        <Tabs.Panel value="mitigation" pt="md">
          {project && <MitigationTab project={project} orgRid={orgId ?? ''} disasterRid={project.disasterId ?? ''} canEdit={!isApplicantViewer} isApplicant={isApplicantRole} />}
        </Tabs.Panel>
      </Tabs>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add/Edit Cost Line
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        title={editingCost ? 'Edit Cost Line' : 'Add Cost Line'}
        size="lg"
      >
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            <Select
              label="Cost Type" required
              description="Category of this cost line item"
              data={COST_TYPE_OPTIONS}
              value={costForm.costType}
              onChange={(v) => setCostForm(prev => ({ ...prev, costType: v || '' }))}
            />
            <Select
              label="Estimated or Actual"
              description="Whether this is a projected estimate or verified actual cost"
              data={EST_ACTUAL_OPTIONS}
              value={costForm.estimatedOrActual}
              onChange={(v) => setCostForm(prev => ({ ...prev, estimatedOrActual: v || 'estimated' }))}
            />
          </SimpleGrid>
          <TextInput
            label="Description"
            description="Brief description of the work, item, or service"
            value={costForm.description}
            onChange={(e) => setCostForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <SimpleGrid cols={3}>
            <TextInput
              label="Quantity"
              description="Number of units (e.g., hours, cubic yards, each)"
              value={costForm.quantity ? Number(String(costForm.quantity).replace(/,/g, '')).toLocaleString() : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                setCostForm(prev => {
                  const qty = parseFloat(raw) || 0;
                  const uc = parseFloat(prev.unitCost) || 0;
                  return { ...prev, quantity: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
                });
              }}
            />
            <TextInput
              label="Unit"
              description="Unit of measure (e.g., hours, CY, LF, EA, LS)"
              value={costForm.unit}
              onChange={(e) => setCostForm(prev => ({ ...prev, unit: e.target.value }))}
            />
            <TextInput
              label="Unit Cost"
              description="Cost per unit"
              value={formatCurrency(costForm.unitCost)}
              onChange={(e) => {
                const raw = stripCurrency(e.target.value);
                setCostForm(prev => {
                  const qty = parseFloat(String(prev.quantity || '').replace(/,/g, '') || '0') || 0;
                  const uc = parseFloat(raw) || 0;
                  return { ...prev, unitCost: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
                });
              }}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput
              label="Total Cost"
              description="Auto-calculated (Qty × Unit Cost) — editable for overrides"
              value={formatCurrency(costForm.totalCost)}
              onChange={(e) => setCostForm(prev => ({ ...prev, totalCost: stripCurrency(e.target.value) }))}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontWeight: 600 } }}
            />
            <Select
              label="CEF Part"
              description="FEMA Cost Estimating Format (CEF) section this cost falls under"
              data={CEF_PART_OPTIONS}
              value={costForm.cefPart}
              onChange={(v) => setCostForm(prev => ({ ...prev, cefPart: v || '' }))}
              clearable
            />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <TextInput
              label="Vendor / Employee"
              description="Company name, contractor, or employee associated with this cost"
              value={costForm.vendorEmployee}
              onChange={(e) => setCostForm(prev => ({ ...prev, vendorEmployee: e.target.value }))}
            />
            <TextInput
              label="Source Reference"
              description="Invoice #, purchase order, timesheet, or other documentation reference"
              value={costForm.sourceReference}
              onChange={(e) => setCostForm(prev => ({ ...prev, sourceReference: e.target.value }))}
            />
          </SimpleGrid>
          <Select
            label="Eligible"
            description="Whether FEMA considers this cost eligible for reimbursement"
            data={ELIGIBLE_OPTIONS}
            value={costForm.eligible}
            onChange={(v) => setCostForm(prev => ({ ...prev, eligible: v || 'true' }))}
          />
          <Textarea
            label="Notes"
            description="Additional context or justification"
            value={costForm.notes}
            onChange={(e) => setCostForm(prev => ({ ...prev, notes: e.target.value }))}
            minRows={2}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setCostModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCost} loading={costSaving}>
              {editingCost ? 'Update' : 'Add'} Cost Line
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add/Edit Insurance Record
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={insModalOpen}
        onClose={() => setInsModalOpen(false)}
        title={editingIns ? 'Edit Insurance Record' : 'Add Insurance Record'}
        size="lg"
      >
        <Tabs defaultValue="policy">
          <Tabs.List>
            <Tabs.Tab value="policy">Policy</Tabs.Tab>
            <Tabs.Tab value="claims">Claims</Tabs.Tab>
            <Tabs.Tab value="om">O&amp;M</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="policy" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput
                  label="Policy Number"
                  description="Unique identifier from the insurance policy"
                  value={insForm.policyNumber}
                  onChange={(e) => setInsForm(prev => ({ ...prev, policyNumber: e.target.value }))}
                />
                <TextInput
                  label="Carrier"
                  description="Insurance company name"
                  value={insForm.carrier}
                  onChange={(e) => setInsForm(prev => ({ ...prev, carrier: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <Select
                  label="Policy Type"
                  description="Type of insurance coverage"
                  data={POLICY_TYPE_OPTIONS}
                  value={insForm.policyType}
                  onChange={(v) => setInsForm(prev => ({ ...prev, policyType: v || '' }))}
                  clearable
                />
                <TextInput
                  label="Coverage Amount"
                  description="Maximum amount covered under this policy"
                  value={formatCurrency(insForm.coverageAmount)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, coverageAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput
                  label="Deductible"
                  description="Amount paid out-of-pocket before coverage begins"
                  value={formatCurrency(insForm.deductible)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, deductible: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
                <TextInput
                  label="Policy Effective Date"
                  type="date"
                  value={insForm.policyEffectiveDate}
                  onChange={(e) => setInsForm(prev => ({ ...prev, policyEffectiveDate: e.target.value }))}
                />
              </SimpleGrid>
              <TextInput
                label="Policy Expiration Date"
                type="date"
                value={insForm.policyExpirationDate}
                onChange={(e) => setInsForm(prev => ({ ...prev, policyExpirationDate: e.target.value }))}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="claims" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <Select
                  label="Claim Filed"
                  data={YES_NO_OPTIONS}
                  value={insForm.claimFiled}
                  onChange={(v) => setInsForm(prev => ({ ...prev, claimFiled: v || 'no' }))}
                />
                <TextInput
                  label="Claim Number"
                  value={insForm.claimNumber}
                  onChange={(e) => setInsForm(prev => ({ ...prev, claimNumber: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput
                  label="Claim Date"
                  type="date"
                  value={insForm.claimDate}
                  onChange={(e) => setInsForm(prev => ({ ...prev, claimDate: e.target.value }))}
                />
                <TextInput
                  label="Settlement Amount"
                  description="Total amount received from insurance settlement"
                  value={formatCurrency(insForm.settlementAmount)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, settlementAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
              </SimpleGrid>
              <TextInput
                label="Settlement Date"
                type="date"
                value={insForm.settlementDate}
                onChange={(e) => setInsForm(prev => ({ ...prev, settlementDate: e.target.value }))}
              />
              <SimpleGrid cols={2}>
                <Select
                  label="Denial Received"
                  data={YES_NO_OPTIONS}
                  value={insForm.denialReceived}
                  onChange={(v) => setInsForm(prev => ({ ...prev, denialReceived: v || 'no' }))}
                />
                <TextInput
                  label="Denial Reason"
                  value={insForm.denialReason}
                  onChange={(e) => setInsForm(prev => ({ ...prev, denialReason: e.target.value }))}
                />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="om" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <Select
                  label="O&M Required"
                  data={YES_NO_OPTIONS}
                  value={insForm.obtainMaintainRequired}
                  onChange={(v) => setInsForm(prev => ({ ...prev, obtainMaintainRequired: v || 'no' }))}
                />
                <Select
                  label="O&M Committed"
                  data={YES_NO_OPTIONS}
                  value={insForm.obtainMaintainCommitted}
                  onChange={(v) => setInsForm(prev => ({ ...prev, obtainMaintainCommitted: v || 'no' }))}
                />
              </SimpleGrid>
              <TextInput
                label="FEMA Deduction Amount"
                description="Amount FEMA deducts from eligible costs due to insurance proceeds"
                value={formatCurrency(insForm.femaDeductionAmount)}
                onChange={(e) => setInsForm(prev => ({ ...prev, femaDeductionAmount: stripCurrency(e.target.value) }))}
                styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setInsModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveInsurance} loading={insSaving}>
            {editingIns ? 'Update' : 'Add'} Insurance Record
          </Button>
        </Group>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Edit Compliance
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={compModalOpen}
        onClose={() => setCompModalOpen(false)}
        title="Edit Compliance"
        size="xl"
      >
        <SimpleGrid cols={2}>
          {/* NEPA column */}
          <Stack gap="sm">
            <Text fw={600} size="sm">NEPA / Environmental</Text>
            <Select
              label="NEPA Required"
              description="Whether a NEPA environmental review is required for this project"
              data={YES_NO_OPTIONS}
              value={compForm.nepaRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaRequired: v || '' }))}
            />
            <Select
              label="NEPA Level"
              description="CatEx = Categorical Exclusion (most common), EA = Environmental Assessment, EIS = Environmental Impact Statement"
              data={NEPA_LEVEL_OPTIONS}
              value={compForm.nepaLevel}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaLevel: v || '' }))}
              clearable
            />
            <Select
              label="NEPA Status"
              description="Current status of the NEPA review process"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.nepaStatus}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaStatus: v || '' }))}
              clearable
            />
            <TextInput
              label="NEPA Completion Date"
              type="date"
              value={compForm.nepaCompletionDate}
              onChange={(e) => setCompForm(prev => ({ ...prev, nepaCompletionDate: e.target.value }))}
            />

            <Divider my="xs" />
            <Text fw={600} size="sm">Floodplain</Text>
            <Select
              label="In Floodplain"
              description="Whether the project site is in a FEMA-designated flood zone"
              data={YES_NO_OPTIONS}
              value={compForm.inFloodplain}
              onChange={(v) => setCompForm(prev => ({ ...prev, inFloodplain: v || '' }))}
            />
            <TextInput
              label="Flood Zone"
              description="FEMA flood zone designation (e.g., A, AE, VE, X)"
              value={compForm.floodZone}
              onChange={(e) => setCompForm(prev => ({ ...prev, floodZone: e.target.value }))}
            />
            <Select
              label="Flood Insurance Required"
              data={YES_NO_OPTIONS}
              value={compForm.floodInsuranceRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, floodInsuranceRequired: v || '' }))}
            />
            <Select
              label="EO 11988 Compliance"
              description="Executive Order 11988 requires agencies to avoid floodplain development where possible"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.eo11988Compliance}
              onChange={(v) => setCompForm(prev => ({ ...prev, eo11988Compliance: v || '' }))}
              clearable
            />
          </Stack>

          {/* Section 106 + Coastal column */}
          <Stack gap="sm">
            <Text fw={600} size="sm">Section 106 / Historic</Text>
            <Select
              label="Section 106 Required"
              description="Whether Section 106 historic preservation review is needed"
              data={YES_NO_OPTIONS}
              value={compForm.section106Required}
              onChange={(v) => setCompForm(prev => ({ ...prev, section106Required: v || '' }))}
            />
            <Select
              label="Section 106 Status"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.section106Status}
              onChange={(v) => setCompForm(prev => ({ ...prev, section106Status: v || '' }))}
              clearable
            />
            <Select
              label="Historic Property"
              description="Whether the site is listed or eligible for the National Register of Historic Places"
              data={YES_NO_OPTIONS}
              value={compForm.historicProperty}
              onChange={(v) => setCompForm(prev => ({ ...prev, historicProperty: v || '' }))}
            />
            <Select
              label="SHPO Consultation"
              description="Status of consultation with the State Historic Preservation Officer"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.shpoConsultation}
              onChange={(v) => setCompForm(prev => ({ ...prev, shpoConsultation: v || '' }))}
              clearable
            />
            <Select
              label="Tribal Consultation"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.tribalConsultation}
              onChange={(v) => setCompForm(prev => ({ ...prev, tribalConsultation: v || '' }))}
              clearable
            />

            <Divider my="xs" />
            <Text fw={600} size="sm">Coastal / Other</Text>
            <Select
              label="Coastal Barrier Area"
              description="Whether the site is within a Coastal Barrier Resources System (CBRS) unit"
              data={YES_NO_OPTIONS}
              value={compForm.coastalBarrierArea}
              onChange={(v) => setCompForm(prev => ({ ...prev, coastalBarrierArea: v || '' }))}
            />
            <Select
              label="Coastal Zone"
              data={YES_NO_OPTIONS}
              value={compForm.coastalZone}
              onChange={(v) => setCompForm(prev => ({ ...prev, coastalZone: v || '' }))}
            />
            <Select
              label="ESA Consultation Required"
              description="Whether Endangered Species Act Section 7 consultation is needed"
              data={YES_NO_OPTIONS}
              value={compForm.esaConsultationRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, esaConsultationRequired: v || '' }))}
            />
            <Select
              label="Hazmat Present"
              description="Whether hazardous materials have been identified at the site"
              data={YES_NO_OPTIONS}
              value={compForm.hazmatPresent}
              onChange={(v) => setCompForm(prev => ({ ...prev, hazmatPresent: v || '' }))}
            />
          </Stack>
        </SimpleGrid>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setCompModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCompliance} loading={compSaving}>
            Save Compliance
          </Button>
        </Group>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add Timeline Event
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        title="Add Timeline Event"
        size="md"
      >
        <Stack gap="sm">
          <Select
            label="Event Type" required
            description="Category of this timeline event"
            data={EVENT_TYPE_OPTIONS}
            value={eventForm.eventType}
            onChange={(v) => setEventForm(prev => ({ ...prev, eventType: v || 'milestone' }))}
          />
          <SimpleGrid cols={2}>
            <TextInput
              label="Event Date"
              description="Date the event occurred or was recorded"
              type="date"
              value={eventForm.eventDate}
              onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))}
            />
            <TextInput
              label="Due Date"
              description="Target completion or deadline date"
              type="date"
              value={eventForm.dueDate}
              onChange={(e) => setEventForm(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </SimpleGrid>
          <Textarea
            label="Description" required
            description="Details about this event or milestone"
            value={eventForm.description}
            onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
            minRows={3}
          />
          <Select
            label="Status"
            description="Current status of this event"
            data={EVENT_STATUS_OPTIONS}
            value={eventForm.status}
            onChange={(v) => setEventForm(prev => ({ ...prev, status: v || 'pending' }))}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setEventModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEvent} loading={eventSaving}>
              Add Event
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Edit Project
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={editProjModalOpen}
        onClose={() => setEditProjModalOpen(false)}
        title="Edit Project"
        size="xl"
      >
        <Tabs value={editProjTab} onChange={setEditProjTab}>
          <Tabs.List>
            <Tabs.Tab value="general">General</Tabs.Tab>
            <Tabs.Tab value="scope">Scope</Tabs.Tab>
            <Tabs.Tab value="costs">Costs</Tabs.Tab>
            <Tabs.Tab value="schedule">Schedule</Tabs.Tab>
            <Tabs.Tab value="status">Status</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput
                  label="Project Number"
                  description="Auto-assigned project identifier"
                  value={editProjForm.projectNumber}
                  readOnly
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontWeight: 700 } }}
                />
                <TextInput
                  label="Title" required
                  description="Descriptive name for the project"
                  value={editProjForm.projectTitle}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, projectTitle: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={3}>
                <Select
                  label="Category"
                  data={CATEGORY_OPTIONS}
                  value={editProjForm.category}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, category: v || '' }))}
                />
                <Select
                  label="Size"
                  data={SIZE_OPTIONS}
                  value={editProjForm.projectSize}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, projectSize: v || '' }))}
                />
                <Select
                  label="Work Type"
                  data={WORK_TYPE_OPTIONS}
                  value={editProjForm.workType}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, workType: v || '' }))}
                  searchable
                  clearable
                />
              </SimpleGrid>
              <TextInput
                label="Address"
                value={editProjForm.projectAddress}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, projectAddress: e.target.value }))}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scope" pt="md">
            <Stack gap="sm">
              <Textarea
                label="Damage Description & Dimensions (DDD)"
                description="Describe all damage including facility name, pre-disaster condition, cause of damage, and dimensions"
                value={editProjForm.damageDescription}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, damageDescription: e.target.value }))}
                autosize minRows={8} maxRows={30}
              />
              <Textarea
                label="Scope of Work (SOW)"
                description="Detailed scope including materials, methods, labor, equipment, and quantities"
                value={editProjForm.scopeOfWork}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, scopeOfWork: e.target.value }))}
                autosize minRows={8} maxRows={30}
              />
              <Textarea
                label="Cost Narrative"
                description="Explain and justify project costs, including methodology for estimates"
                value={editProjForm.costNarrative}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, costNarrative: e.target.value }))}
                autosize minRows={4} maxRows={20}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="costs" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput
                  label="Estimated Cost"
                  description="Projected total eligible cost"
                  value={formatCurrency(editProjForm.estimatedCost)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, estimatedCost: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
                <TextInput
                  label="Actual Cost"
                  description="Verified total cost to date"
                  value={formatCurrency(editProjForm.actualCost)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, actualCost: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                />
              </SimpleGrid>
              <TextInput
                label="Federal Share %"
                description="Percentage of eligible costs covered by federal funding (typically 75%)"
                value={editProjForm.federalSharePct}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, federalSharePct: e.target.value }))}
                styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="schedule" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <TextInput
                  label="Work Start Date"
                  description="Date work began or is planned to begin"
                  type="date"
                  value={editProjForm.workStartDate}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, workStartDate: e.target.value }))}
                />
                <TextInput
                  label="Deadline"
                  description="Required completion date per FEMA timeline"
                  type="date"
                  value={editProjForm.deadline}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, deadline: e.target.value }))}
                />
              </SimpleGrid>
              <TextInput
                label="Completion % Estimate"
                description="Estimated percentage of work completed"
                value={editProjForm.completionPctEstimate}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, completionPctEstimate: e.target.value }))}
                styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="status" pt="md">
            <Stack gap="sm">
              {!isApplicantRole && (
                <TextInput
                  label="Assigned Specialist"
                  description="FEMA PDMG or state specialist assigned to this project"
                  value={editProjForm.assignedSpecialist}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, assignedSpecialist: e.target.value }))}
                />
              )}
              {!isApplicantRole ? (
                <SimpleGrid cols={2}>
                  <Select
                    label="Phase"
                    description="Current FEMA processing phase"
                    data={PHASE_OPTIONS}
                    value={editProjForm.phase}
                    onChange={(v) => setEditProjForm(prev => ({ ...prev, phase: v || 'formulation' }))}
                  />
                  <Select
                    label="Status"
                    description="Administrative status within the current phase"
                    data={PROJECT_STATUS_OPTIONS}
                    value={editProjForm.status}
                    onChange={(v) => setEditProjForm(prev => ({ ...prev, status: v || 'draft' }))}
                  />
                </SimpleGrid>
              ) : (
                <SimpleGrid cols={2}>
                  <TextInput label="Phase" value={editProjForm.phase} disabled />
                  <TextInput label="Status" value={editProjForm.status} disabled />
                </SimpleGrid>
              )}
              <Textarea
                label="Notes"
                description="Internal notes and comments"
                value={editProjForm.notes}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, notes: e.target.value }))}
                minRows={4}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setEditProjModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProject} loading={editProjSaving}>
            Save Changes
          </Button>
        </Group>
      </Modal>

      {/* ── Add Task Modal ─────────────────────────────────────── */}
      <Modal opened={taskModalOpen} onClose={() => setTaskModalOpen(false)} title="Add Task" size="md">
        <Stack gap="sm">
          <Textarea label="Description" required
            description="What needs to be done"
            value={taskForm.description}
            onChange={e => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <TextInput label="Due Date" type="date"
            description="When this task needs to be completed"
            value={taskForm.dueDate}
            onChange={e => setTaskForm(prev => ({ ...prev, dueDate: e.target.value }))}
          />
          <Select label="Status"
            description="Current progress status"
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Complete' },
            ]}
            value={taskForm.status}
            onChange={val => setTaskForm(prev => ({ ...prev, status: val || 'pending' }))}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button loading={taskSaving} onClick={handleSaveTask}>Create Task</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Upload Tagging Modal ──────────────────────────────── */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => { setUploadModalOpen(false); setUploadFile(null); }}
        title="Upload Document"
        size="md"
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
            <Group gap="sm">
              <IconFile size={20} />
              <Stack gap={0}>
                <Text size="sm" fw={500}>{uploadFile?.name}</Text>
                <Text size="xs" c="dimmed">{uploadFile ? `${(uploadFile.size / 1024).toFixed(0)} KB` : ''}</Text>
              </Stack>
            </Group>
          </Paper>

          <Select
            label="What is this document?"
            description="Tag this upload to a checklist item — it will be marked as complete and added to the project timeline"
            data={DOC_TYPE_OPTIONS}
            value={uploadDocType}
            onChange={v => setUploadDocType(v || '')}
            searchable
          />

          <Textarea
            label="Notes"
            description="Optional notes about this document"
            value={uploadNotes}
            onChange={e => setUploadNotes(e.target.value)}
            minRows={2}
          />

          {/* AI Document Analysis */}
          {uploadFile && (uploadFile.type.startsWith('image/') || uploadFile.type === 'application/pdf') && (
            <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-violet-0)' }}>
              <Group justify="space-between" mb={aiDocResult ? 'xs' : 0}>
                <Group gap="xs">
                  <IconRobot size={16} color="var(--mantine-color-violet-6)" />
                  <Text size="sm" fw={600} c="violet">AI Document Analysis</Text>
                </Group>
                <Group gap="xs">
                  <Button size="xs" variant="light" color="violet" loading={aiAnalyzing}
                    onClick={handleAiAnalyze} leftSection={<IconRobot size={12} />}>
                    {aiDocResult ? 'Re-analyze' : 'Analyze'}
                  </Button>
                  <Button size="xs" variant="filled" color="violet" loading={aiAnalyzing}
                    onClick={handleAiExtractCosts} leftSection={<IconScan size={12} />}>
                    Extract Costs
                  </Button>
                </Group>
              </Group>
              {aiDocResult && (
                <Paper p="xs" mt="xs" style={{ background: 'white', maxHeight: 300, overflow: 'auto' }}>
                  <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{aiDocResult}</Text>
                </Paper>
              )}
            </Paper>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}>Cancel</Button>
            <Button loading={uploading} onClick={handleUpload} leftSection={<IconUpload size={14} />}>
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Checklist Completion Modal (non-document items) ────── */}
      <Modal
        opened={checkModalOpen}
        onClose={() => setCheckModalOpen(false)}
        title={`Mark Complete: ${checkingItem?.label || ''}`}
        size="md"
      >
        <Stack gap="sm">
          {checkingItem && (
            <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-green-0)', border: '1px solid var(--mantine-color-green-3)' }}>
              <Text size="sm">{checkingItem.description}</Text>
            </Paper>
          )}

          <Textarea
            label="Notes (optional)"
            description="Add any context — who performed the review, reference numbers, etc."
            value={checkNotes}
            onChange={e => setCheckNotes(e.target.value)}
            minRows={2}
          />

          <Text size="xs" c="dimmed">
            This will create a timeline event and mark the checklist item as complete.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setCheckModalOpen(false)}>Cancel</Button>
            <Button color="green" loading={checkSaving} onClick={handleCheckOff}
              leftSection={<IconCheck size={14} />}>
              Mark as Complete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Revision Request Modal ──────────────────────────────── */}
      <Modal
        opened={!!revisionItem}
        onClose={() => setRevisionItem(null)}
        title={`Request Revision: ${revisionItem?.label || ''}`}
        size="md"
      >
        <Stack gap="sm">
          {revisionItem && (
            <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-orange-0)', border: '1px solid var(--mantine-color-orange-3)' }}>
              <Text size="sm">{revisionItem.description}</Text>
            </Paper>
          )}

          <Textarea
            label="Feedback for the applicant"
            description="Explain what needs to be corrected, updated, or re-submitted"
            placeholder="e.g., The damage description is missing dimensions. Please add square footage measurements for each affected area."
            value={revisionNote}
            onChange={e => setRevisionNote(e.target.value)}
            minRows={3}
            required
          />

          <Text size="xs" c="dimmed">
            This will mark the item as needing revision and notify the applicant. They will need to re-submit to clear it.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setRevisionItem(null)}>Cancel</Button>
            <Button color="orange" loading={revisionSaving} onClick={handleRequestRevision}
              leftSection={<IconArrowLeft size={14} />}
              disabled={!revisionNote.trim()}>
              Request Revision
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── AI Cost Extraction Review Modal ────────────────────── */}
      <Modal
        opened={extractModalOpen}
        onClose={() => { setExtractModalOpen(false); setExtractedCosts([]); }}
        title={`Review Extracted Costs — ${extractSource}`}
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-violet-0)', border: '1px solid var(--mantine-color-violet-2)' }}>
            <Group gap="xs">
              <IconRobot size={16} color="var(--mantine-color-violet-6)" />
              <Text size="sm" c="violet.7" fw={500}>
                AI extracted {extractedCosts.length} cost line{extractedCosts.length !== 1 ? 's' : ''} from &quot;{extractSource}&quot;
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>Review and edit before importing. Remove any incorrect items.</Text>
          </Paper>

          {extractedCosts.map((cost, idx) => (
            <Paper key={idx} p="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Badge size="sm" color={COST_TYPE_COLORS[cost.costType] || 'gray'}>
                    {COST_TYPE_LABELS[cost.costType] || cost.costType}
                  </Badge>
                  {cost.cefPart && <Badge size="xs" variant="outline">CEF Part {cost.cefPart}</Badge>}
                </Group>
                <ActionIcon size="sm" variant="subtle" color="red"
                  onClick={() => setExtractedCosts(prev => prev.filter((_, i) => i !== idx))}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={2} spacing="xs">
                <TextInput size="xs" label="Description" value={cost.description}
                  onChange={e => {
                    const next = [...extractedCosts];
                    next[idx] = { ...next[idx], description: e.target.value };
                    setExtractedCosts(next);
                  }} />
                <SimpleGrid cols={3} spacing="xs">
                  <TextInput size="xs" label="Qty" value={cost.quantity}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], quantity: e.target.value };
                      setExtractedCosts(next);
                    }} />
                  <TextInput size="xs" label="Unit" value={cost.unit}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], unit: e.target.value };
                      setExtractedCosts(next);
                    }} />
                  <TextInput size="xs" label="Total"
                    value={formatCurrency(cost.totalCost)}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], totalCost: stripCurrency(e.target.value) };
                      setExtractedCosts(next);
                    }}
                    styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
                </SimpleGrid>
              </SimpleGrid>
            </Paper>
          ))}

          {extractedCosts.length === 0 && (
            <Text c="dimmed" ta="center" py="md">All items removed. Nothing to import.</Text>
          )}

          <Divider />
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Total: {formatCurrency(String(extractedCosts.reduce((sum, c) => sum + (parseFloat(c.totalCost) || 0), 0)))}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setExtractModalOpen(false); setExtractedCosts([]); }}>
                Cancel
              </Button>
              <Button color="violet" loading={extracting}
                disabled={extractedCosts.length === 0}
                leftSection={<IconRobot size={14} />}
                onClick={handleImportExtractedCosts}>
                Import {extractedCosts.length} Cost{extractedCosts.length !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── Scan Receipt Review Modal ─────────────────────── */}
      <Modal
        opened={scanModalOpen}
        onClose={() => { setScanModalOpen(false); setScannedCosts([]); }}
        title={<Group gap="xs"><IconScan size={20} color="var(--mantine-color-violet-6)" /> Review Scanned Costs — {scanSource}</Group>}
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-violet-0)', border: '1px solid var(--mantine-color-violet-2)' }}>
            <Group gap="xs">
              <IconRobot size={16} color="var(--mantine-color-violet-6)" />
              <Text size="sm" c="violet.7" fw={500}>
                AI found {scannedCosts.length} cost line{scannedCosts.length !== 1 ? 's' : ''} in &quot;{scanSource}&quot;
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>Review each item — edit the category, amounts, or description. Remove anything incorrect before confirming.</Text>
          </Paper>

          {scannedCosts.map((cost, idx) => (
            <Paper key={idx} p="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Badge size="sm" color={COST_TYPE_COLORS[cost.costType] || 'gray'}>
                    {COST_TYPE_LABELS[cost.costType] || cost.costType}
                  </Badge>
                  {cost.cefPart && <Badge size="xs" variant="outline">CEF Part {cost.cefPart}</Badge>}
                </Group>
                <ActionIcon size="sm" variant="subtle" color="red"
                  onClick={() => setScannedCosts(prev => prev.filter((_, i) => i !== idx))}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={2} spacing="xs">
                <TextInput size="xs" label="Description" value={cost.description}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], description: e.target.value };
                    setScannedCosts(next);
                  }} />
                <Select size="xs" label="Category" data={[
                  { value: 'labor', label: 'Labor (CEF A)' },
                  { value: 'equipment', label: 'Equipment (CEF B)' },
                  { value: 'materials', label: 'Materials (CEF C)' },
                  { value: 'rented_equip', label: 'Rented Equipment (CEF D)' },
                  { value: 'contracts', label: 'Contracts (CEF E)' },
                  { value: 'other', label: 'Other / Admin (CEF F)' },
                  { value: 'donated', label: 'Donated Resources' },
                ]}
                  value={cost.costType}
                  onChange={v => {
                    const cefMap: Record<string, string> = { labor: 'A', equipment: 'B', materials: 'C', rented_equip: 'D', contracts: 'E', other: 'F', donated: '' };
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], costType: v ?? 'other', cefPart: cefMap[v ?? 'other'] ?? '' };
                    setScannedCosts(next);
                  }} />
              </SimpleGrid>
              <SimpleGrid cols={4} spacing="xs" mt="xs">
                <TextInput size="xs" label="Qty" value={cost.quantity}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], quantity: e.target.value };
                    setScannedCosts(next);
                  }} />
                <TextInput size="xs" label="Unit" value={cost.unit}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], unit: e.target.value };
                    setScannedCosts(next);
                  }} />
                <TextInput size="xs" label="Unit Cost"
                  value={formatCurrency(cost.unitCost)}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], unitCost: stripCurrency(e.target.value) };
                    setScannedCosts(next);
                  }}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
                <TextInput size="xs" label="Total"
                  value={formatCurrency(cost.totalCost)}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], totalCost: stripCurrency(e.target.value) };
                    setScannedCosts(next);
                  }}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              </SimpleGrid>
            </Paper>
          ))}

          {scannedCosts.length === 0 && (
            <Text c="dimmed" ta="center" py="md">All items removed. Nothing to import.</Text>
          )}

          <Divider />
          <Group justify="space-between">
            <Text size="sm" fw={500} style={{ fontFamily: 'DM Mono, monospace' }}>
              Total: {formatCurrency(String(scannedCosts.reduce((sum, c) => sum + (parseFloat(c.totalCost) || 0), 0)))}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setScanModalOpen(false); setScannedCosts([]); }}>
                Cancel
              </Button>
              <Button color="green" loading={scanning}
                disabled={scannedCosts.length === 0}
                leftSection={<IconCheck size={14} />}
                onClick={handleImportScannedCosts}>
                Confirm &amp; Import {scannedCosts.length} Cost{scannedCosts.length !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── AI Assist Modal ────────────────────────────────── */}
      <Modal
        opened={aiAssistOpen}
        onClose={() => setAiAssistOpen(false)}
        title={<Group gap="xs"><IconRobot size={20} color="var(--mantine-color-violet-6)" /> AI Project Review</Group>}
        size="lg"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {aiAssistLoading ? (
          <Stack align="center" py="xl" gap="sm">
            <Loader size={32} color="violet" />
            <Text size="sm" c="dimmed">Analyzing project data...</Text>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Paper p="sm" withBorder style={{ background: 'var(--mantine-color-violet-0)', border: '1px solid var(--mantine-color-violet-2)' }}>
              <Text size="xs" c="violet.7">AI review of {project?.projectNumber} — {project?.projectTitle}</Text>
            </Paper>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: aiAssistResult
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/^### (.*$)/gm, '<h4 style="margin:12px 0 4px;color:var(--mantine-color-violet-7)">$1</h4>')
                .replace(/^## (.*$)/gm, '<h3 style="margin:16px 0 6px;color:var(--mantine-color-violet-8)">$1</h3>')
                .replace(/^# (.*$)/gm, '<h3 style="margin:16px 0 6px;color:var(--mantine-color-violet-8)">$1</h3>')
                .replace(/^- (.*$)/gm, '<div style="padding-left:16px">• $1</div>')
                .replace(/^\d+\. (.*$)/gm, '<div style="padding-left:16px">$&</div>')
              }} />
            <Group justify="flex-end" mt="sm">
              <Button variant="light" color="violet" leftSection={<IconRobot size={14} />}
                loading={aiAssistLoading} onClick={handleAiAssist}>
                Re-analyze
              </Button>
              <Button variant="default" onClick={() => setAiAssistOpen(false)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Timesheet Import Modal ───────────────────────────── */}
      <Modal
        opened={timesheetImportOpen}
        onClose={() => { setTimesheetImportOpen(false); setTimesheetSelected(new Set()); setTsSearch(''); setTsCatFilter(null); }}
        title="Import Timesheets into Project"
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Select time entries to assign to {project?.projectNumber} &mdash; {project?.projectTitle}
          </Text>
          <Text size="xs" c="dimmed">
            Only supervisor-approved timesheets are shown. Pending entries must be approved in Time &amp; Labor before import.
          </Text>

          {/* Filter bar */}
          <Group gap="sm">
            <TextInput
              size="xs" placeholder="Search employee or activity..."
              value={tsSearch} onChange={e => setTsSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <Select
              size="xs" placeholder="Category" clearable
              value={tsCatFilter} onChange={v => setTsCatFilter(v)}
              data={[
                { value: 'A', label: 'Cat A — Debris' },
                { value: 'B', label: 'Cat B — Emergency' },
                { value: 'C', label: 'Cat C — Roads' },
                { value: 'D', label: 'Cat D — Water' },
                { value: 'E', label: 'Cat E — Buildings' },
                { value: 'F', label: 'Cat F — Utilities' },
                { value: 'G', label: 'Cat G — Parks' },
              ]}
              w={180}
            />
          </Group>

          {/* Unassigned labor table */}
          {unassignedLabor.length === 0 ? (
            <Paper p="lg" withBorder>
              <Text size="sm" c="dimmed" ta="center">
                No unassigned time entries found{tsCatFilter ? ` for Category ${tsCatFilter}` : ''}{tsSearch ? ` matching "${tsSearch}"` : ''}.
              </Text>
            </Paper>
          ) : (
            <Paper p="sm" withBorder>
              <Table striped highlightOnHover fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        size="xs"
                        checked={unassignedLabor.length > 0 && unassignedLabor.every(r => timesheetSelected.has(r.Rid))}
                        indeterminate={unassignedLabor.some(r => timesheetSelected.has(r.Rid)) && !unassignedLabor.every(r => timesheetSelected.has(r.Rid))}
                        onChange={() => {
                          const allSelected = unassignedLabor.every(r => timesheetSelected.has(r.Rid));
                          if (allSelected) {
                            setTimesheetSelected(new Set());
                          } else {
                            setTimesheetSelected(new Set(unassignedLabor.map(r => r.Rid)));
                          }
                        }}
                      />
                    </Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Activity</Table.Th>
                    <Table.Th>Cat</Table.Th>
                    <Table.Th>Logged Hrs</Table.Th>
                    <Table.Th>Appr Hrs</Table.Th>
                    <Table.Th>Rate</Table.Th>
                    <Table.Th>Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {unassignedLabor.map(lr => {
                    const matchesCat = project?.category && lr.category === project.category;
                    return (
                      <Table.Tr
                        key={lr.Rid}
                        style={matchesCat ? { background: 'var(--mantine-color-green-0)' } : undefined}
                      >
                        <Table.Td>
                          <Checkbox
                            size="xs"
                            checked={timesheetSelected.has(lr.Rid)}
                            onChange={() => {
                              setTimesheetSelected(prev => {
                                const next = new Set(prev);
                                if (next.has(lr.Rid)) next.delete(lr.Rid);
                                else next.add(lr.Rid);
                                return next;
                              });
                            }}
                          />
                        </Table.Td>
                        <Table.Td>{lr.workDate || '-'}</Table.Td>
                        <Table.Td>{lr.employeeName || '-'}</Table.Td>
                        <Table.Td>{lr.employeeTitle || '-'}</Table.Td>
                        <Table.Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lr.activityDescription || '-'}
                        </Table.Td>
                        <Table.Td>
                          {lr.category ? (
                            <Badge size="xs" variant="light" color={DAMAGE_CATEGORY_COLORS[lr.category as DamageCategory] || 'gray'}>
                              {lr.category}
                            </Badge>
                          ) : '-'}
                        </Table.Td>
                        <Table.Td>{num(lr.hoursRegular) + num(lr.hoursOvertime)}</Table.Td>
                        <Table.Td>
                          {(() => {
                            const aR = lr.approvedRegHours ? parseFloat(lr.approvedRegHours) : num(lr.hoursRegular);
                            const aO = lr.approvedOTHours ? parseFloat(lr.approvedOTHours) : num(lr.hoursOvertime);
                            const approvedTotal = aR + aO;
                            const loggedTotal = num(lr.hoursRegular) + num(lr.hoursOvertime);
                            const isReduced = approvedTotal < loggedTotal;
                            return (
                              <Text size="sm" fw={isReduced ? 600 : undefined} c={isReduced ? 'orange' : undefined}>
                                {approvedTotal}
                              </Text>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td>{fmt(num(lr.rateRegular))}</Table.Td>
                        <Table.Td fw={600}>{fmt(num(lr.approvedCost || lr.totalCost))}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* Already-assigned entries from other projects */}
          {otherProjectLabor.length > 0 && (
            <Paper p="sm" withBorder style={{ opacity: 0.6 }}>
              <Text size="xs" c="dimmed" mb="xs">
                {otherProjectLabor.length} time {otherProjectLabor.length === 1 ? 'entry' : 'entries'} already assigned to other projects
              </Text>
              {otherProjectLabor.slice(0, 5).map(lr => {
                const assignedProj = projects.find(p => String(p.Rid) === lr.projectId);
                return (
                  <Text key={lr.Rid} size="xs" c="dimmed">
                    {lr.employeeName} — {lr.workDate} &rarr; Assigned to {assignedProj?.projectNumber || `Project #${lr.projectId}`}
                  </Text>
                );
              })}
              {otherProjectLabor.length > 5 && (
                <Text size="xs" c="dimmed">...and {otherProjectLabor.length - 5} more</Text>
              )}
            </Paper>
          )}

          {/* Footer with selection summary */}
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {timesheetSelected.size > 0 ? (
                <>
                  {timesheetSelected.size} {timesheetSelected.size === 1 ? 'entry' : 'entries'} selected
                  {' '}&mdash;{' '}
                  {laborRecords.filter(r => timesheetSelected.has(r.Rid)).reduce((s, r) => {
                    const aR = r.approvedRegHours ? parseFloat(r.approvedRegHours) : num(r.hoursRegular);
                    const aO = r.approvedOTHours ? parseFloat(r.approvedOTHours) : num(r.hoursOvertime);
                    return s + aR + aO;
                  }, 0)} hrs
                  {' '}&mdash;{' '}
                  {fmt(laborRecords.filter(r => timesheetSelected.has(r.Rid)).reduce((s, r) => s + num(r.approvedCost || r.totalCost), 0))}
                </>
              ) : (
                <Text size="sm" c="dimmed">Select time entries to import</Text>
              )}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setTimesheetImportOpen(false); setTimesheetSelected(new Set()); }}>
                Cancel
              </Button>
              <Button
                color="violet"
                loading={timesheetImporting}
                disabled={timesheetSelected.size === 0}
                leftSection={<IconCheck size={14} />}
                onClick={handleTimesheetImport}
              >
                Import Selected ({timesheetSelected.size})
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
